import React, { useEffect, useState, useRef, useCallback, useMemo, lazy, Suspense } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from '../store'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { io, Socket } from 'socket.io-client'
import TailwindModal from './ui/TailwindModal'
import { useWeb3Context } from '../contexts/web3Context'
import { invalidateMvmReinforceAvailability } from '../hooks/useMvmReinforceAvailable'
import config from '../utils/config'
import { preventMonsterAttack, checkMonsterAttackStatus, getResources } from '../store/user/actions'
import { fetchCharacters } from '../common/state/inventory/action'
import { syncResourcesFromResponse } from '../hooks/useResourceSync'
import { onShowAlert } from '../store/utiles/actions'
import { addNotification, removeNotification } from '../common/state/notifications/reducer'
import { fetchNotifications } from '../common/state/notifications/action'
import {
  mvmDefenderSelectAndConfirm,
  mvmDefenderTowerMarch,
  mvmAutoDefend,
  getMvMPendingAttacks,
  getMvMReinforcementEligibility,
  getMvMPendingRosters,
  joinMvMRally,
  joinMvMReinforcement,
  requestDefenseHelp,
  mvmAttackArrived,
  respondToRansomRequestApi,
  getPendingRansomRequestsApi,
  type RansomResources,
  getMvMRallyJoinEligibility,
} from '../common/api'
import { registerMvMPendingSocket, mvmWorldAttackPrecheck } from '../common/mvmPendingSocket'
import { chooseCastleConquer, chooseCastleRaid, getMyCastleConquerOffer } from '../common/allianceApi'
import {
  MVM_CASTLE_AUTO_RESOLVE_EVENT,
  mvmCastleAutoResolveDedupeKey,
  parseCastleAutoResolveFromApi,
  type MvmCastleAutoResolveDetail,
} from '../common/mvmCastleAutoResolve'
import { restMvmCancelDefenderTowerMarch } from '../common/mvmPendingRest'
import { getAuthToken } from '../utils/authToken'
import type { MvMPendingAttackEntry } from '../common/state/mvmRoster/state'
import {
  mergeOffenseRoster,
  mergeDefenseRoster,
  setMvMPendingRostersFromServer,
  removeMvMPendingAttack,
} from '../common/state/mvmRoster/reducer'
import {
  clearMvMarchCharacterLocks,
  unlockMvMarchCharactersForAttack,
} from '../common/state/mvmMarchLocks/reducer'
import { ALLIED_PARTICIPANT_MAX_CHARACTERS, MAIN_PARTICIPANT_MAX_CHARACTERS } from '../constants/allianceCombat'
import { mvmMinCastleAttackerBreadLoaves } from '../constants/mvmBread'
import { normalizeServerMapType, type MapType } from '../config/hexTerrain'
import { mvmAutoResolveBattleToast } from '../common/mvmBattleEndedToast'
import { clearActiveMvMBattle, loadActiveMvMBattle } from '../common/mvmBattleResume'
import { isOnBattlePath } from '../common/battleRoutes'
import {
  MVM_ALLIANCE_BATTLE_PANEL_DECOR_H_PX,
  MVM_ALLIANCE_BATTLE_PANEL_DECOR_OUTSET_PX,
  MVM_ALLIANCE_BATTLE_PANEL_TOP_EXTRA_UP_PX,
  MVM_ATTACK_NOTI_BG_URL,
  MVM_INCOMING_ATTACK_PANEL_BG_URL,
  MVM_INCOMING_ATTACK_PANEL_BOTTOM_URL,
  MVM_INCOMING_ATTACK_PANEL_TOP_URL,
} from '../pages/hex-battle/constants'
import { mvmDefeatBackButtonStyle } from './MvMBattle/mvmBattleResultUi'
import { formatMvMTurnSummary } from './MvMBattle/formatMvMTurnSummary'
import type { MvMTurnResult } from '../hooks/useMvMBattleSocket'
import { WorldMapAutoBattleResultModal } from '../game/world-map/WorldMapAutoBattleResultModal'
import type { AutoBattleResult } from '../game/world-map/worldMapApi'
import {
  enterBarbarianVillageDefense,
  autoBarbarianVillageDefense,
  getBarbarianVillageDefenseAlerts,
} from '../game/world-map/worldMapApi'
import { alert } from './Alert/Alert'
import { dispatchMvmMapClear, dispatchReturnMarchFromApi } from '../common/mvmMapSync'
import {
  mvmPendingArrivalEndMs,
  mvmPendingDisplayPhase,
  type MvMPendingLike,
} from '../common/mvmRallyDisplay'
import { invalidateMvmRallyJoinAvailability } from '../hooks/useMvmRallyJoinAvailable'

const MVM_BATTLE_STARTED_EVENT = 'mvm:battle-started-wallet'
const LazyDefenseRewardAlert = lazy(() => import('./DefenseRewardAlert'))
const LazyAttackCharacterSelectionModal = lazy(() => import('./WorldMap/AttackCharacterSelectionModal'))
const LazyMvMVictoryModal = lazy(() =>
  import('./MvMBattle/MvMVictoryModal').then((module) => ({ default: module.MvMVictoryModal })),
)
const LazyMvMDefeatModal = lazy(() =>
  import('./MvMBattle/MvMDefeatModal').then((module) => ({ default: module.MvMDefeatModal })),
)

/** Dispatched when MvM auto-battle HUD appears/disappears — world map draws sword FX (banner hidden). */
const WORLD_MAP_MVM_AUTO_SWORD_EVENT = 'eclipsera:world-map-mvm-auto-sword'

function dispatchWorldMapMvmAutoSword(
  active: boolean,
  opts?: {
    attackId?: string | null
    battleId?: string | null
    attackType?: string
    /** Tower / castle hex for map sword FX (tower PvP auto-resolve is near-instant). */
    targetQ?: number
    targetR?: number
    /** When dispatched with `active: false`, settles the world-map power bar to win/loss before clear. */
    outcome?: 'won' | 'lost'
  },
) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent(WORLD_MAP_MVM_AUTO_SWORD_EVENT, {
      detail: active
        ? {
            active: true,
            attackId: opts?.attackId ?? null,
            battleId: opts?.battleId ?? null,
            attackType: opts?.attackType,
            targetQ: opts?.targetQ,
            targetR: opts?.targetR,
          }
        : { active: false, outcome: opts?.outcome ?? null },
    }),
  )
}

interface AttackInfo {
  type: 'barbarian' | 'monster' | 'mvm'
  castleId?: number
  targetBuilding?: string
  totalDamage?: number
  startTime?: number
  endTime?: number
  walletAddress: string
  attackId?: string
  attackerWallet?: string
  attackerUsername?: string
  attackerAllianceName?: string
  /** Castle owner under attack (used for "Request help" and banner copy). */
  defenderWallet?: string
  /** Top red banner: castle defender + allied reinforcers only — not attackers / rally joiners. */
  defenseIncomingBanner?: boolean
  /** Attacker-side banner: army is marching home after battle. */
  attackerReturningBanner?: boolean
  /** Tower PvP defender: champions marching home after cancel or after hex battle. */
  defenderReturningBanner?: boolean
  /** Enemy alliance rally — first countdown is gather window; then march ETA updates via socket */
  rallyMode?: boolean
  /** `march` once rally window ended (or march socket) so copy shows “Arrives in” not stuck rally at 0:00. */
  mvmCountdownPhase?: 'rally' | 'march'
  /** When set, countdown + phase are derived each tick (rally→march handoff before next refetch). */
  mvmPendingSnapshot?: MvMPendingLike
  /** Boundary tower PvP vs castle siege — used for defender copy and toasts. */
  mvmAttackType?: 'castle' | 'tower_pvp' | 'barbarian_village'
  /** From GET /mvm/pending-attacks — defender already committed a march to the tower. */
  towerMarchCommitted?: boolean
  /** Raw server status for gating tower deploy button. */
  mvmStatusFromServer?: string
  /** Tower PvP: owner was in another MvM hex when the march started (alert still delivered immediately). */
  defenderBusyInOtherBattle?: boolean
  defenderAttackerBusyAutoDefense?: boolean
  /** Defender march ETA (GET /mvm/pending-attacks) — used for tower “hold tower, wait attacker” copy. */
  defenderArrivesAtIso?: string
  /** Barbarian-village target hex — shown in the banner so simultaneous owned-village attacks are distinguishable. */
  toQ?: number
  toR?: number
}

/**
 * Tower defense: defender reached the tower first — keep a “waiting for attacker” alert until the hex battle
 * starts (or auto-resolve). Uses REST `mvmStatusFromServer` when present; falls back to the march snapshot so
 * the banner stays up before the first pending refetch after `mvm:incoming-attack`.
 */
function towerDefenderHoldUntilAttackerResolves(a: AttackInfo, now: number): boolean {
  if (a.type !== 'mvm' || a.mvmAttackType !== 'tower_pvp' || !a.defenseIncomingBanner) return false
  const st = String(a.mvmStatusFromServer || '')
  const snapSt = String(a.mvmPendingSnapshot?.status || '')
  if (st === 'arrived' || snapSt === 'arrived') return true
  const attIso = a.mvmPendingSnapshot?.arrivesAt
  const attAt = attIso ? new Date(attIso).getTime() : NaN
  const travelLike =
    st === 'traveling' || snapSt === 'traveling' || (!st && (snapSt === '' || snapSt === 'traveling'))
  if (travelLike && Number.isFinite(attAt) && now >= attAt) return true
  return false
}

function mvmAttackEndMsForUi(a: AttackInfo, now: number): number | undefined {
  if (a.type === 'mvm' && a.mvmPendingSnapshot) {
    if (towerDefenderHoldUntilAttackerResolves(a, now)) {
      return Number.POSITIVE_INFINITY
    }
    return mvmPendingArrivalEndMs(a.mvmPendingSnapshot, now)
  }
  return normalizeTime(a.endTime)
}

const normalizeTime = (time: any): number | undefined => {
  if (!time) return undefined
  if (typeof time === 'number') return time
  if (typeof time === 'string') return new Date(time).getTime()
  if (time instanceof Date) return time.getTime()
  return undefined
}

const normWallet = (w: string | undefined | null) => (w || '').toString().trim().toLowerCase()

function attackInfoShallowEqual(a: AttackInfo | undefined, b: AttackInfo | undefined): boolean {
  if (a === b) return true
  if (!a || !b) return false
  const aKeys = Object.keys(a) as Array<keyof AttackInfo>
  const bKeys = Object.keys(b) as Array<keyof AttackInfo>
  if (aKeys.length !== bKeys.length) return false
  for (const key of aKeys) {
    if (a[key] !== b[key]) return false
  }
  return true
}

function setAttackIfChanged(map: Map<string, AttackInfo>, key: string, value: AttackInfo): Map<string, AttackInfo> {
  if (attackInfoShallowEqual(map.get(key), value)) return map
  const next = new Map(map)
  next.set(key, value)
  return next
}

function deleteAttackIfPresent(map: Map<string, AttackInfo>, key: string): Map<string, AttackInfo> {
  if (!map.has(key)) return map
  const next = new Map(map)
  next.delete(key)
  return next
}

/** Stable id for a closed barbarian-village banner: its (village) attack id + arrival time. */
function villageBannerDismissKey(attackId: string | undefined, endTime: number | undefined): string {
  return `${attackId ?? ''}:${endTime ?? 0}`
}

function attackMapShallowEqual(a: Map<string, AttackInfo>, b: Map<string, AttackInfo>): boolean {
  if (a === b) return true
  if (a.size !== b.size) return false
  for (const [key, value] of a) {
    if (!attackInfoShallowEqual(value, b.get(key))) return false
  }
  return true
}

function autoBattleHudEqual(
  a: { battleId: string; attackId?: string | null; round: number; line: string; attackType?: string } | null,
  b: { battleId: string; attackId?: string | null; round: number; line: string; attackType?: string } | null,
): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.battleId === b.battleId &&
    a.attackId === b.attackId &&
    a.round === b.round &&
    a.line === b.line &&
    a.attackType === b.attackType
  )
}

function buildMvMTurnResultFromAutoProgressPayload(data: any): MvMTurnResult | null {
  const ts = data?.turnSummary
  if (!ts || typeof ts !== 'object') return null
  return {
    characterIndex: ts.characterIndex ?? null,
    side: String(ts.side || 'defender'),
    action: String(ts.action || ''),
    moveTo: ts.moveTo ?? null,
    targetCharIndex: ts.targetCharIndex ?? null,
    damage: Number(ts.damage) || 0,
    isCrit: !!ts.isCrit,
    effects: Array.isArray(ts.effects) ? ts.effects : [],
    targetDied: !!ts.targetDied,
    characterDied: !!ts.characterDied,
    battleEnded: !!ts.battleEnded,
    winner: ts.winner ?? null,
    winReason: ts.winReason ?? null,
    nextTurn: ts.nextTurn ?? null,
    roundNumber: Number(data.roundNumber) || 1,
    currentTurnIndex: Number(data.currentTurnIndex) || 0,
    characters: [],
    watchtowers: null,
  }
}

/** Persist “help already sent” per attack so the banner button stays hidden after click (and after remount). */
const MVM_DEFENSE_HELP_STORAGE_PREFIX = 'eclipsera_mvm_defense_help:'

function isMvMDefenseHelpRequested(attackId: string | undefined | null): boolean {
  if (!attackId || typeof sessionStorage === 'undefined') return false
  try {
    return sessionStorage.getItem(`${MVM_DEFENSE_HELP_STORAGE_PREFIX}${attackId}`) === '1'
  } catch {
    return false
  }
}

/** Stacked above the fixed MvM banner (`zIndex` 9999) for tower auto-resolve result modals. */
const MVM_AUTO_RESULT_OVERLAY_Z = 12_000

const GlobalAttackAlert: React.FC = () => {
  const { t } = useTranslation()
  const { address } = useWeb3Context()
  const dispatch = useDispatch<any>()
  const location = useLocation()
  const navigate = useNavigate()
  const socketRef = useRef<Socket | null>(null)
  const socketWalletRef = useRef<string | null>(null)
  const locationPathRef = useRef(location.pathname)
  locationPathRef.current = location.pathname
  /** One same-browser resume attempt per mounted socket; reset on a failed lookup so reconnect retries. */
  const mvmResumeCheckedRef = useRef(false)
  /** One stable MvM socket for all routes after `/start` — avoids missing `mvm:attack-arrived` on pathname churn. */
  const walletConnectedForMainLand = !!address && location.pathname !== '/start'
  const hexBattleContextRef = useRef<{ path: string; battleId: string | null }>({ path: '', battleId: null })
  hexBattleContextRef.current = {
    path: location.pathname,
    battleId:
      location.state && typeof (location.state as { battleId?: unknown }).battleId !== 'undefined'
        ? String((location.state as { battleId?: unknown }).battleId)
        : null,
  }
  const [activeAttacks, setActiveAttacks] = useState<Map<string, AttackInfo>>(new Map())
  const [isDefending, setIsDefending] = useState<Map<string, boolean>>(new Map())
  const [timeDisplayKey, setTimeDisplayKey] = useState(0)
  const lastTimeDisplayRef = useRef<string>('')
  const activeAttacksRef = useRef(activeAttacks)
  activeAttacksRef.current = activeAttacks
  const refetchMvmPendingRef = useRef<(() => void) | null>(null)
  /**
   * Incoming-attack banners (village + castle) the owner has explicitly closed (X). Keyed by
   * `${attackId}:${endTime}` → Unix ms when the dismiss expires (2 min). After expiry the next
   * 30 s poll / socket event re-shows the banner. A *future* attack on the same village (new
   * arrival time) gets a different key and is never suppressed.
   */
  const dismissedBannersUntilRef = useRef<Map<string, number>>(new Map())
  const [showRewardAlert, setShowRewardAlert] = useState(false)
  const [rewardData, setRewardData] = useState<{
    reward: any
    attackType: 'barbarian' | 'monster'
    castleId?: number
  } | null>(null)

  // Defender choice modal: "Enter battle" (manual hex) or "Auto defend" (server offline power resolve).
  const [defenderChoiceOpen, setDefenderChoiceOpen] = useState(false)
  const [defenderMvmAttackType, setDefenderMvmAttackType] = useState<'castle' | 'tower_pvp'>('castle')
  /**
   * Owned barbarian-village defense: the attacker has ARRIVED and is waiting. The hex battle does NOT exist
   * yet — the owner decides. Enter Battle → server builds the hex and BOTH navigate in; Auto Battle → pure
   * power-resolve (no redirect; result modal + the attacker's army returns home). Keyed by village, not battle.
   */
  const [villageDefense, setVillageDefense] = useState<{ worldId: string; villageId: string; attackerLabel?: string } | null>(null)
  const [villageDefenseSubmitting, setVillageDefenseSubmitting] = useState(false)
  const [towerMarchDeployOpen, setTowerMarchDeployOpen] = useState(false)
  const [towerMarchDeployAttackId, setTowerMarchDeployAttackId] = useState<string | null>(null)
  const [towerMarchSubmitting, setTowerMarchSubmitting] = useState(false)
  const [towerRecallBusyAttackId, setTowerRecallBusyAttackId] = useState<string | null>(null)
  const [defenderAttackId, setDefenderAttackId] = useState<string | null>(null)
  const defenderAttackIdRef = useRef<string | null>(null)
  defenderAttackIdRef.current = defenderAttackId
  const [defenderSubmitting, setDefenderSubmitting] = useState(false)
  // If user picks "Enter Battle", show character selection
  const [defenderManualModalOpen, setDefenderManualModalOpen] = useState(false)
  const [helpRequestBusyAttackId, setHelpRequestBusyAttackId] = useState<string | null>(null)
  const [rallyJoinSubmitting, setRallyJoinSubmitting] = useState(false)
  const [reinforceSubmitting, setReinforceSubmitting] = useState(false)
  /** Bumps after successful request-defense-help so banner re-renders and hides the button. */
  const [, bumpDefenseHelpRequestedUi] = useState(0)
  /** Attack IDs where defender already entered battle — blocks duplicate ENTER prompts */
  const defenderBattleEnteredRef = useRef<Set<string>>(new Set())
  /** While character picker is open for this attack, do not re-open the choice modal (refetch / duplicate socket) */
  const defenderPickingCharactersRef = useRef<string | null>(null)

  const [mvmRallyInvite, setMvmRallyInvite] = useState<{
    attackId: string
    leaderName?: string | null
    targetName?: string | null
    arrivesAt?: string
    rallyPhaseEndAt?: string
    marchTravelTimeMs?: number
    leaderCharacterCount?: number
    rallyJoinMinBread?: number
  } | null>(null)
  const [mvmRallyJoinModalOpen, setMvmRallyJoinModalOpen] = useState(false)
  const rallyJoinAttackIdRef = useRef<string | null>(null)
  const [rallyJoinMarchMs, setRallyJoinMarchMs] = useState(15_000)
  const [rallyJoinLeaderMinBread, setRallyJoinLeaderMinBread] = useState(0)

  const [mvmReinforceInvite, setMvmReinforceInvite] = useState<{
    attackId: string
    message?: string
    arrivesAt?: string
    helpRequestedByDefender?: boolean
    defenderWallet?: string
    worldId?: string
  } | null>(null)
  const [mvmReinforceModalOpen, setMvmReinforceModalOpen] = useState(false)
  /** Server-computed march time from this ally's castle to the besieged castle — sizes the bread input. */
  const [reinforceMarchMs, setReinforceMarchMs] = useState(15_000)
  const reinforceAttackIdRef = useRef<string | null>(null)

  // Ransom requests incoming to this user (as prisoner owner)
  const [ransomRequests, setRansomRequests] = useState<Array<{
    ransomId: string
    characterInstanceId: string
    characterName: string
    characterImg: string
    holderWallet: string
    holderNick: string
    resources: RansomResources
    resourceSummary: string
  }>>([])
  const [ransomRespondingId, setRansomRespondingId] = useState<string | null>(null)

  /** Global outcome (tower auto-resolve, full hex while off battlefield, etc.). */
  const [mvmTowerAutoResolveModal, setMvmTowerAutoResolveModal] = useState<null | {
    won: boolean
    you: 'attacker' | 'defender'
    message: string
    characterXp: number
    userXp: number
    showResourceLoot?: boolean
    seizedResources?: Record<string, number> | null
    attackType?: string
    allianceTaskBattle?: boolean
  }>(null)
  /**
   * `mvm:battle-ended` opens the modal — but we hold it back by this many ms so the world-map
   * power balance bar has time to ease to 100% / 0% before the modal covers it. Matches the bar
   * transition window (~700 ms) plus dwell time.
   */
  const MVM_AUTO_VIEW_OUTCOME_BEFORE_MODAL_MS = 1100
  const mvmTowerAutoResolveModalDelayRef = useRef<number | null>(null)
  const mvmAutoResolveModalDedupeRef = useRef<Set<string>>(new Set())
  const castleOutcomeFetchAttemptedRef = useRef<Set<string>>(new Set())

  /**
   * Post-victory castle Conquer/Raid choice for results shown OUTSIDE `/hex-battle` (offline / busy
   * auto-resolve, or a hex battle that ended while the player was elsewhere). `mvm:castle-conquer-offer`
   * is a wallet-room push with no battle key we can match on, so the ref scopes it to the result modal
   * currently being opened — otherwise an offer the hex HUD already owns, or one left over from an
   * earlier siege, would attach itself to the next victory modal.
   */
  const [castleConquerOffer, setCastleConquerOffer] = useState<{
    offerId: string
    allianceName?: string
  } | null>(null)
  const castleConquerEligibleRef = useRef(false)

  const scheduleMvmAutoResolveModal = useCallback((payload: MvmCastleAutoResolveDetail) => {
    const dedupeKey = mvmCastleAutoResolveDedupeKey(payload)
    if (mvmAutoResolveModalDedupeRef.current.has(dedupeKey)) return
    mvmAutoResolveModalDedupeRef.current.add(dedupeKey)

    /** Same gating as MvMBattleHUD: attacker side, `castle`, not an alliance task (loot optional). */
    castleConquerEligibleRef.current =
      payload.won === true &&
      payload.you === 'attacker' &&
      (payload.attackType || 'castle') === 'castle' &&
      payload.allianceTaskBattle !== true
    setCastleConquerOffer(null)

    dispatchWorldMapMvmAutoSword(false, { outcome: payload.won ? 'won' : 'lost' })

    if (mvmTowerAutoResolveModalDelayRef.current != null) {
      window.clearTimeout(mvmTowerAutoResolveModalDelayRef.current)
    }
    mvmTowerAutoResolveModalDelayRef.current = window.setTimeout(() => {
      mvmTowerAutoResolveModalDelayRef.current = null
      setMvmTowerAutoResolveModal({
        won: payload.won,
        you: payload.you,
        message: payload.message,
        characterXp: payload.characterXp,
        userXp: payload.userXp,
        showResourceLoot: payload.showResourceLoot === true,
        seizedResources: payload.seizedResources ?? null,
        attackType: payload.attackType || 'castle',
        allianceTaskBattle: payload.allianceTaskBattle === true,
      })
    }, MVM_AUTO_VIEW_OUTCOME_BEFORE_MODAL_MS)
  }, [])

  /**
   * When the server finishes an offline auto-battle before the client gets `mvm:battle-ended`, the
   * return-march banner can appear without a victory/defeat modal. Re-fetch via attack-arrived once.
   */
  const fetchCastleOutcomeForReturningMarch = useCallback(
    (attackId: string, wallet: string) => {
      const aid = attackId?.trim()
      const wl = (wallet || '').trim()
      if (!aid || !wl) return
      if (castleOutcomeFetchAttemptedRef.current.has(aid)) return
      const dedupeKey = mvmCastleAutoResolveDedupeKey({ attackId: aid, battleId: undefined })
      if (mvmAutoResolveModalDedupeRef.current.has(dedupeKey)) return
      castleOutcomeFetchAttemptedRef.current.add(aid)
      void mvmAttackArrived(wl, aid)
        .then((data) => {
          const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : null
          dispatchReturnMarchFromApi(payload)
          const parsed = parseCastleAutoResolveFromApi(payload, wl)
          if (!parsed) return
          dispatch(unlockMvMarchCharactersForAttack(parsed.attackId))
          dispatch(removeMvMPendingAttack(parsed.attackId))
          scheduleMvmAutoResolveModal(parsed)
        })
        .catch(() => {})
    },
    [dispatch, scheduleMvmAutoResolveModal],
  )
  useEffect(
    () => () => {
      if (mvmTowerAutoResolveModalDelayRef.current != null) {
        window.clearTimeout(mvmTowerAutoResolveModalDelayRef.current)
        mvmTowerAutoResolveModalDelayRef.current = null
      }
    },
    [],
  )

  /** Castle siege: HTTP path may deliver the outcome before `mvm:battle-ended` reaches the wallet room. */
  useEffect(() => {
    const onCastleAutoResolve = (ev: Event) => {
      const detail = (ev as CustomEvent<MvmCastleAutoResolveDetail>).detail
      if (!detail || typeof detail.won !== 'boolean') return
      if (detail.attackId) {
        dispatch(unlockMvMarchCharactersForAttack(detail.attackId))
        dispatch(removeMvMPendingAttack(detail.attackId))
      }
      scheduleMvmAutoResolveModal(detail)
      if (detail.attackId && address) {
        window.setTimeout(() => {
          void mvmAttackArrived(address, detail.attackId)
            .then((d) =>
              dispatchReturnMarchFromApi(d && typeof d === 'object' ? (d as Record<string, unknown>) : null),
            )
            .catch(() => {})
        }, 400)
      }
    }
    window.addEventListener(MVM_CASTLE_AUTO_RESOLVE_EVENT, onCastleAutoResolve)
    return () => window.removeEventListener(MVM_CASTLE_AUTO_RESOLVE_EVENT, onCastleAutoResolve)
  }, [dispatch, scheduleMvmAutoResolveModal, address])

  /**
   * Attacker castle win shown OUTSIDE the hex page (offline / busy auto-resolve, or a hex battle that
   * finished while the player was elsewhere): the server created a conquer offer and is waiting for a
   * choice, so this victory modal needs the same Conquer/Raid buttons MvMBattleHUD shows on `/hex-battle`.
   * Gating matches the HUD exactly — attacker side, `castle`, not an alliance-task battle.
   */
  const awaitCastleConquerOffer =
    mvmTowerAutoResolveModal?.won === true &&
    mvmTowerAutoResolveModal.you === 'attacker' &&
    (mvmTowerAutoResolveModal.attackType || 'castle') === 'castle' &&
    mvmTowerAutoResolveModal.allianceTaskBattle !== true

  useEffect(() => {
    const onOffer = (e: Event) => {
      if (!castleConquerEligibleRef.current) return
      const detail = (e as CustomEvent).detail as { offerId?: string; allianceName?: string } | undefined
      if (detail?.offerId) {
        setCastleConquerOffer({ offerId: detail.offerId, allianceName: detail.allianceName })
      }
    }
    window.addEventListener('castle-conquer-offer', onOffer)
    return () => window.removeEventListener('castle-conquer-offer', onOffer)
  }, [])
  /** REST fallback so a socket push that arrived before this modal opened still shows the choice. */
  useEffect(() => {
    if (!awaitCastleConquerOffer || castleConquerOffer || !address) return
    let cancelled = false
    let tries = 0
    const poll = async () => {
      if (cancelled) return
      try {
        const r = await getMyCastleConquerOffer(address)
        if (!cancelled && r?.offer?.offerId) {
          setCastleConquerOffer({
            offerId: r.offer.offerId,
            allianceName: r.offer.allianceName ?? undefined,
          })
          return
        }
      } catch {
        /* ignore — the socket push may still deliver it */
      }
      tries += 1
      if (!cancelled && tries < 6) window.setTimeout(poll, 700)
    }
    void poll()
    return () => {
      cancelled = true
    }
  }, [awaitCastleConquerOffer, castleConquerOffer, address])

  const handleGlobalCastleConquer = useCallback(async () => {
    if (!address || !castleConquerOffer) return
    // Result lets the modal tell "request sent to leader" from the leader-is-attacker auto-join.
    return chooseCastleConquer(address, castleConquerOffer.offerId).catch(() => undefined)
  }, [address, castleConquerOffer])
  const handleGlobalCastleRaid = useCallback(() => {
    if (!address || !castleConquerOffer) return
    void chooseCastleRaid(address, castleConquerOffer.offerId).catch(() => {})
  }, [address, castleConquerOffer])
  const [worldMapPveResultModal, setWorldMapPveResultModal] = useState<null | {
    result: AutoBattleResult
    type: 'barbarian' | 'defense_tower'
  }>(null)
  /**
   * Stays visible until `mvm:battle-ended` for the same battle (by `battleId`, or `attackId` when sent).
   * Tower offline auto emits `mvm:tower-pvp-auto-battle` (no hex progress ticks); hex AI uses `mvm:auto-battle-progress`.
   */
  const [mvmAutoBattleProgressHud, setMvmAutoBattleProgressHud] = useState<null | {
    battleId: string
    attackId?: string | null
    round: number
    line: string
    attackType: string
  }>(null)

  useEffect(() => {
    if (location.pathname === '/hex-battle') {
      dispatchWorldMapMvmAutoSword(false)
      return
    }
    if (!mvmAutoBattleProgressHud) {
      dispatchWorldMapMvmAutoSword(false)
      return
    }
    /** Tower offline auto: `mvm:tower-pvp-auto-battle` dispatches sword with hex coords (instant resolve). */
    if (mvmAutoBattleProgressHud.attackType === 'tower_pvp') return
    const aid = mvmAutoBattleProgressHud.attackId != null ? String(mvmAutoBattleProgressHud.attackId).trim() : ''
    const bid = mvmAutoBattleProgressHud.battleId != null ? String(mvmAutoBattleProgressHud.battleId).trim() : ''
    if (!aid && !bid) return
    dispatchWorldMapMvmAutoSword(true, {
      attackId: aid || null,
      battleId: bid || null,
      attackType: mvmAutoBattleProgressHud.attackType,
    })
  }, [mvmAutoBattleProgressHud, location.pathname])

  const shouldOfferDefenderChoice = useCallback((attackId: string) => {
    if (!attackId) return false
    if (locationPathRef.current === '/hex-battle') return false
    if (defenderBattleEnteredRef.current.has(attackId)) return false
    if (defenderPickingCharactersRef.current === attackId) return false
    return true
  }, [])
  const shouldOfferDefenderChoiceRef = useRef(shouldOfferDefenderChoice)
  shouldOfferDefenderChoiceRef.current = shouldOfferDefenderChoice

  const mvmRosterByAttackId = useSelector((s: RootState) => s.mvmRoster?.byAttackId ?? {})

  /**
   * Current notification list, read through a ref so the socket effect below (which is set up once,
   * with an empty-ish dep list) can retire stale march tickers without re-subscribing every time a
   * notification arrives.
   */
  const notifications = useSelector((s: RootState) => s.notifications?.notifications ?? [])
  const notificationsRef = useRef(notifications)
  notificationsRef.current = notifications

  /**
   * Ally-deployment banner dismissals, by attackId. This banner is purely informational ("you will
   * be pulled into the battle automatically"), so a close stays closed for that deployment — unlike
   * the incoming-attack strip, which re-shows after 2 min because missing it has a real cost.
   * Keyed rather than a single boolean so dismissing one deployment still surfaces the next.
   */
  const [dismissedAllyHintIds, setDismissedAllyHintIds] = useState<Set<string>>(new Set())

  const dismissAllyDeploymentHint = useCallback((attackId: string | undefined | null) => {
    const aid = (attackId || '').trim()
    if (!aid) return
    setDismissedAllyHintIds((prev) => {
      if (prev.has(aid)) return prev
      const next = new Set(prev)
      next.add(aid)
      return next
    })
  }, [])

  const mvmAllyDeploymentHint = useMemo(() => {
    if (!address) return null
    const me = normWallet(address)
    const skip = new Set(['battle_started', 'battle_creating', 'finished'])
    for (const [rosterKey, entry] of Object.entries(mvmRosterByAttackId)) {
      const st = entry.status || entry.offense?.status || entry.defense?.status || ''
      if (skip.has(String(st))) continue
      /** Fall back to the map key so the banner is always dismissable, even on a lean entry. */
      const attackId = entry.attackId || rosterKey
      if (dismissedAllyHintIds.has(attackId)) continue
      const off = entry.offense
      if (off?.joiners?.some((j) => normWallet(j.walletAddress) === me)) {
        return {
          kind: 'rally' as const,
          leaderName: off.leader?.displayName || 'your leader',
          attackId,
        }
      }
      const def = entry.defense
      if (def?.reinforcements?.some((r) => normWallet(r.walletAddress) === me)) {
        return {
          kind: 'reinforce' as const,
          castleLabel: def.castleOwner?.displayName || 'an ally castle',
          attackId,
        }
      }
    }
    return null
  }, [address, mvmRosterByAttackId, dismissedAllyHintIds])

  const handleRequestAllianceHelp = useCallback(
    async (attackId: string | undefined | null) => {
      if (!address || !attackId) return
      setHelpRequestBusyAttackId(attackId)
      try {
        await requestDefenseHelp(address, attackId)
        try {
          sessionStorage.setItem(`${MVM_DEFENSE_HELP_STORAGE_PREFIX}${attackId}`, '1')
        } catch {
          /* ignore quota / private mode */
        }
        try {
          window.dispatchEvent(new CustomEvent('eclipsera:mvm-defense-help-sent', { detail: { attackId } }))
        } catch {
          /* ignore */
        }
        bumpDefenseHelpRequestedUi((n) => n + 1)
        dispatch(onShowAlert(t('worldMap.mvmHelpSentToast'), 'success'))
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || t('worldMap.mvmHelpRequestFail')
        dispatch(onShowAlert(msg, 'error'))
      } finally {
        setHelpRequestBusyAttackId(null)
      }
    },
    [address, dispatch, t],
  )

  const handleDefenderEnterBattle = useCallback(() => {
    if (defenderAttackId) defenderPickingCharactersRef.current = defenderAttackId
    setDefenderChoiceOpen(false)
    setDefenderManualModalOpen(true)
  }, [defenderAttackId])

  /** Apply auto-defend HTTP outcome: modal, return march, pending cleanup. */
  const finalizeCastleAutoDefendUi = useCallback(
    (payload: Record<string, unknown> | null | undefined, attackId: string) => {
      if (!payload || !address) return false
      const parsed = parseCastleAutoResolveFromApi(payload, address)
      if (!parsed) return false
      dispatchReturnMarchFromApi(payload)
      dispatch(unlockMvMarchCharactersForAttack(parsed.attackId))
      dispatch(removeMvMPendingAttack(parsed.attackId))
      defenderBattleEnteredRef.current.add(attackId)
      defenderPickingCharactersRef.current = null
      setDefenderChoiceOpen(false)
      setDefenderManualModalOpen(false)
      setDefenderAttackId(null)
      setDefenderMvmAttackType('castle')
      setActiveAttacks((prev) => deleteAttackIfPresent(prev, `mvm-${attackId}`))
      scheduleMvmAutoResolveModal(parsed)
      refetchMvmPendingRef.current?.()
      return true
    },
    [address, dispatch, scheduleMvmAutoResolveModal],
  )

  /** When auto-defend returns `battlePreparing`, poll auto-defend until the finished battle is readable. */
  const pollCastleAutoDefendOutcome = useCallback(async (wallet: string, attackId: string, maxMs = 12000) => {
    const start = Date.now()
    while (Date.now() - start < maxMs) {
      try {
        const data = await mvmAutoDefend(wallet, attackId)
        const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : null
        if (payload && parseCastleAutoResolveFromApi(payload, wallet)) return payload
      } catch {
        /* retry */
      }
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 450)
      })
    }
    return null
  }, [])

  const handleDefenderAutoDefend = useCallback(async () => {
    const attackId = defenderAttackId ?? defenderAttackIdRef.current
    if (!address || !attackId || defenderSubmitting) return
    setDefenderSubmitting(true)
    /**
     * Bound the request: `mvmAutoDefend` falls back socket→REST, but if both stall the button would
     * stay disabled and the modal would hang open (the symptom players hit). Race a timeout so the UI
     * always recovers; the battle still resolves server-side and `mvm:battle-ended` closes the modal
     * + shows the result even if this call timed out.
     */
    const AUTO_DEFEND_TIMEOUT_MS = 25000
    let autoDefendTimer: ReturnType<typeof setTimeout> | undefined
    try {
      const result = (await Promise.race([
        mvmAutoDefend(address, attackId),
        new Promise<never>((_resolve, reject) => {
          autoDefendTimer = setTimeout(() => reject(new Error('auto-defend timed out')), AUTO_DEFEND_TIMEOUT_MS)
        }),
      ])) as Record<string, unknown>
      if (finalizeCastleAutoDefendUi(result, attackId)) return
      if (result?.botDefense === true) {
        // A real hex battle is now running with a bot piloting this castle's garrison. Nothing to enter:
        // the world-map auto-battle banner tracks it and `mvm:battle-ended` brings the result modal.
        defenderBattleEnteredRef.current.add(attackId)
        defenderPickingCharactersRef.current = null
        setDefenderChoiceOpen(false)
        setDefenderManualModalOpen(false)
        setDefenderAttackId(null)
        setDefenderMvmAttackType('castle')
        setActiveAttacks((prev) => deleteAttackIfPresent(prev, `mvm-${attackId}`))
        dispatch(
          onShowAlert(
            String(result?.message || '').trim() ||
              t('modals.globalAttack.mvmDefenderBotDefense', {
                defaultValue: 'Your garrison is defending the castle automatically.',
              }),
            'info',
          ),
        )
        refetchMvmPendingRef.current?.()
        return
      }
      if (result?.battlePreparing === true) {
        const polled = await pollCastleAutoDefendOutcome(address, attackId)
        if (finalizeCastleAutoDefendUi(polled, attackId)) return
      }
      const msg = String(result?.message || '').trim()
      if (msg) {
        dispatch(onShowAlert(msg, 'info'))
      } else {
        dispatch(onShowAlert(t('modals.globalAttack.mvmDefenderAutoDefendFail'), 'error'))
      }
    } catch (err: any) {
      dispatch(
        onShowAlert(
          err?.response?.data?.message || err?.message || t('modals.globalAttack.mvmDefenderAutoDefendFail'),
          'error',
        ),
      )
    } finally {
      if (autoDefendTimer) clearTimeout(autoDefendTimer)
      setDefenderSubmitting(false)
    }
  }, [
    address,
    defenderAttackId,
    defenderSubmitting,
    dispatch,
    finalizeCastleAutoDefendUi,
    pollCastleAutoDefendOutcome,
    t,
  ])

  const handleDefenderCharacterSelect = useCallback(async (characters: any[], opts?: { breadLoaves?: number }) => {
    if (!address || !defenderAttackId || defenderSubmitting) return
    setDefenderSubmitting(true)
    try {
      const selectedCharacterIds = characters.map((c: any) => c._id)
      const characterTemplateIds = characters.map((c: any) => (c.characterId || c.character?.characterId || 'sakura').toLowerCase())
      const breadLoaves = Math.max(0, Math.floor(Number(opts?.breadLoaves) || 0))
      const result = await mvmDefenderSelectAndConfirm(
        address,
        defenderAttackId,
        selectedCharacterIds,
        undefined,
        characterTemplateIds,
        breadLoaves,
      )
      if (result.battleId) {
        defenderBattleEnteredRef.current.add(defenderAttackId)
        defenderPickingCharactersRef.current = null
        const mapType = normalizeServerMapType(String(result.battle?.mapType))
        // Navigate FIRST so defender lands on battle page before modal closes
        navigate('/hex-battle', {
          replace: true,
          state: {
            battleId: result.battleId,
            selectedCharacterIds,
            characterTemplateIds,
            mapType,
            initialBattle: result.battle,
          }
        })
      } else if (result?.battlePreparing) {
        // The hex is already being built by the other side's confirm or an auto-resolve; the
        // `mvm:battle-started` socket takes the defender in the moment it is ready.
        dispatch(
          onShowAlert(
            t('worldMap.mvmBattlePreparing', {
              defaultValue: 'The battle is being prepared — you will enter as soon as it is ready.',
            }),
            'info',
          ),
        )
      }
      setDefenderChoiceOpen(false)
      setDefenderManualModalOpen(false)
      setDefenderAttackId(null)
      setDefenderMvmAttackType('castle')
      setActiveAttacks(prev => deleteAttackIfPresent(prev, `mvm-${defenderAttackId}`))
    } catch (err: any) {
      dispatch(onShowAlert(err?.response?.data?.message || t('worldMap.mvmConfirmBattleFail'), 'error'))
    } finally {
      setDefenderSubmitting(false)
    }
  }, [address, defenderAttackId, defenderSubmitting, navigate, dispatch, t])

  const handleTowerMarchCharacterSelect = useCallback(
    async (characters: any[]) => {
      if (!address || !towerMarchDeployAttackId || towerMarchSubmitting || !characters?.length) return
      setTowerMarchSubmitting(true)
      try {
        const selectedCharacterIds = characters.map((c: any) => c._id)
        const characterTemplateIds = characters.map((c: any) =>
          (c.characterId || c.character?.characterId || 'sakura').toLowerCase(),
        )
        await mvmDefenderTowerMarch(address, towerMarchDeployAttackId, selectedCharacterIds, undefined, characterTemplateIds, 'main')
        dispatch(
          onShowAlert(
            t('worldMap.mvmTowerDefenderMarchStarted', {
              defaultValue: 'Your champions are marching to the defense tower.',
            }),
            'success',
          ),
        )
        setTowerMarchDeployOpen(false)
        setTowerMarchDeployAttackId(null)
        refetchMvmPendingRef.current?.()
      } catch (err: any) {
        dispatch(onShowAlert(err?.response?.data?.message || err?.message || t('worldMap.mvmConfirmBattleFail'), 'error'))
      } finally {
        setTowerMarchSubmitting(false)
      }
    },
    [address, towerMarchDeployAttackId, towerMarchSubmitting, dispatch, t],
  )

  const handleRecallDefenderTowerMarch = useCallback(
    async (recallAttackId: string) => {
      if (!address || !recallAttackId || towerRecallBusyAttackId) return
      setTowerRecallBusyAttackId(recallAttackId)
      try {
        await restMvmCancelDefenderTowerMarch(address, recallAttackId)
        dispatch(
          onShowAlert(
            t('worldMap.mvmTowerRecallSuccess', {
              defaultValue: 'Your champions have returned to the castle.',
            }),
            'success',
          ),
        )
        refetchMvmPendingRef.current?.()
      } catch (err: any) {
        dispatch(
          onShowAlert(
            err?.response?.data?.message ||
            err?.message ||
            t('worldMap.mvmTowerRecallFail', { defaultValue: 'Could not recall champions.' }),
            'error',
          ),
        )
      } finally {
        setTowerRecallBusyAttackId(null)
      }
    },
    [address, towerRecallBusyAttackId, dispatch, t],
  )

  const handleRallyJoinCharacters = useCallback(async (characters: any[], opts?: { breadLoaves?: number }) => {
    const attackId = rallyJoinAttackIdRef.current
    if (!address || !attackId || !characters?.length || rallyJoinSubmitting) return
    setRallyJoinSubmitting(true)
    try {
      const selectedCharacterIds = characters.map((c: any) => c._id)
      const characterTemplateIds = characters.map((c: any) =>
        (c.characterId || c.character?.characterId || 'sakura').toLowerCase(),
      )
      const breadLoaves = Math.max(0, Math.floor(Number(opts?.breadLoaves) || 0))
      await joinMvMRally(address, attackId, selectedCharacterIds, characterTemplateIds, undefined, breadLoaves)
      // The rally mail's button reads a cached verdict; it must drop the moment this wallet is in.
      invalidateMvmRallyJoinAvailability(attackId)
      dispatch(onShowAlert(t('worldMap.mvmJoinRallySuccess'), 'success'))
      setMvmRallyJoinModalOpen(false)
      rallyJoinAttackIdRef.current = null
    } catch (err: any) {
      dispatch(onShowAlert(err?.response?.data?.message || t('worldMap.mvmJoinRallyFail'), 'error'))
    } finally {
      setRallyJoinSubmitting(false)
    }
  }, [address, dispatch, rallyJoinSubmitting, t])

  const handleReinforceCharacters = useCallback(async (characters: any[], opts?: { breadLoaves?: number }) => {
    const attackId = reinforceAttackIdRef.current
    if (!address || !attackId || !characters?.length || reinforceSubmitting) return
    setReinforceSubmitting(true)
    try {
      const selectedCharacterIds = characters.map((c: any) => c._id)
      const characterTemplateIds = characters.map((c: any) =>
        (c.characterId || c.character?.characterId || 'sakura').toLowerCase(),
      )
      const breadLoaves = Math.max(0, Math.floor(Number(opts?.breadLoaves) || 0))
      await joinMvMReinforcement(address, attackId, selectedCharacterIds, characterTemplateIds, breadLoaves)
      /** Champions are committed — drop the cached verdict so the mail's button stops offering it. */
      invalidateMvmReinforceAvailability(attackId)
      dispatch(onShowAlert(t('worldMap.mvmReinforceSuccess'), 'success'))
      setMvmReinforceModalOpen(false)
      reinforceAttackIdRef.current = null
    } catch (err: any) {
      dispatch(onShowAlert(err?.response?.data?.message || t('worldMap.mvmReinforceFail'), 'error'))
    } finally {
      setReinforceSubmitting(false)
    }
  }, [address, dispatch, reinforceSubmitting, t])

  /**
   * "Help defend" from a persistent notification (see GlobalMailPanel). This is the catch-up
   * entry point for allies who were offline or dismissed the real-time reinforcement modal: they can
   * still join at any time until the battle actually ends. We ask the server whether reinforcements are
   * still open for US — if so we reopen the normal invite → character selection flow, otherwise we say
   * exactly why the window is closed.
   *
   * This MUST NOT be checked against `getMvMPendingAttacks`: that endpoint returns only sieges the
   * viewer already participates in, and an ally who has not joined yet never appears in one. The lookup
   * therefore missed every first-time press and reported "the battle has already ended" while the
   * attacker was still marching to the castle. `getMvMReinforcementEligibility` runs the same rules the
   * join itself enforces, so the button and the join can never disagree.
   */
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = ((e as CustomEvent).detail || {}) as {
        attackId?: string
        defenderWallet?: string
        worldId?: string
        message?: string
        arrivesAt?: string
        helpRequestedByDefender?: boolean
      }
      const attackId = detail?.attackId
      if (!attackId || !address) return
      // The castle owner defends via their own incoming-attack flow, not as a reinforcer.
      if (detail.defenderWallet && normWallet(detail.defenderWallet) === normWallet(address)) return
      getMvMReinforcementEligibility(address, attackId)
        .then((verdict) => {
          if (verdict?.reinforceable) {
            setMvmReinforceInvite({
              attackId,
              message: typeof detail.message === 'string' ? detail.message : undefined,
              /** Prefer the live ETA — the mail's copy is a snapshot from when it was written. */
              arrivesAt:
                verdict.arrivesAt ||
                (typeof detail.arrivesAt === 'string' ? detail.arrivesAt : undefined),
              helpRequestedByDefender: !!detail.helpRequestedByDefender,
              defenderWallet:
                verdict.defenderWallet ||
                (typeof detail.defenderWallet === 'string' ? detail.defenderWallet : undefined),
              worldId:
                verdict.worldId || (typeof detail.worldId === 'string' ? detail.worldId : undefined),
            })
            return
          }
          /** Say WHY — "the battle ended" was previously shown for every one of these. */
          const REASON_COPY: Record<string, { key: string; defaultValue: string }> = {
            already_joined: {
              key: 'worldMap.mvmReinforceAlreadyJoined',
              defaultValue: 'You have already sent reinforcements to this castle.',
            },
            cap_reached: {
              key: 'worldMap.mvmReinforceCapReached',
              defaultValue: 'This defense already has the maximum number of reinforcing allies.',
            },
            not_in_alliance: {
              key: 'worldMap.mvmReinforceNotInAlliance',
              defaultValue: 'You must be in the defender\u2019s alliance to reinforce this castle.',
            },
            is_castle_owner: {
              key: 'worldMap.mvmReinforceIsOwner',
              defaultValue: 'This is your castle — defend it from your own incoming attack panel.',
            },
            /**
             * The march is live but this phase no longer takes reinforcements — a rally past its
             * gather window, or troops already committed and travelling. Reporting "the battle has
             * already ended" here was the exact opposite of the truth: the enemy is still on their
             * way and nothing has been fought yet. Applies to attacker and defender rallies alike.
             */
            phase_closed: {
              key: 'worldMap.mvmReinforcePhaseClosed',
              defaultValue:
                'Reinforcements are closed for this march — the armies are already on their way. The battle has not started yet.',
            },
            not_found: {
              key: 'worldMap.mvmReinforceNotFound',
              defaultValue: 'This march is no longer active.',
            },
          }
          /**
           * The verdict just came back negative, so the cached "yes" that let this button render is
           * stale (verdicts live ~30s, and a phase can close inside that window). Drop it so the
           * button disappears instead of inviting the same dead-end click again.
           */
          invalidateMvmReinforceAvailability(attackId)
          /**
           * "Phase closed" covers opposite situations — the fight is being fought, or it is over — and one
           * line cannot be right for both. Read the phase the server reports and say exactly that.
           */
          const phase = String(verdict?.pendingStatus || '')
          // Allies may march for as long as the fight lasts, so a closed phase means the fight is over
          // (or the row is still catching up with a finished battle) or the march was called off.
          const phaseCopy =
            verdict?.reason === 'phase_closed'
              ? phase === 'returning' || phase === 'completed' || phase === 'battle_started' || phase === 'battle_creating'
                ? t('worldMap.mvmReinforceBattleOver', {
                    defaultValue: 'This battle is over — the armies are already returning home.',
                  })
                : phase === 'cancelled'
                  ? t('worldMap.mvmReinforceCancelled', { defaultValue: 'This march was called off.' })
                  : null
              : verdict?.reason === 'support_tower_destroyed'
                ? t('worldMap.mvmReinforceSupportTowerDown', {
                    defaultValue: 'Their Support tower has fallen — this side can no longer receive allies.',
                  })
                : null
          const copy = phaseCopy ? null : REASON_COPY[verdict?.reason || '']
          /**
           * Prefer the server's own explanation over the generic line: it knows the phase and stays
           * right about reasons this client has not been taught yet. Only with neither a mapped reason
           * nor a server message do we claim the battle ended.
           */
          const serverMessage =
            typeof verdict?.message === 'string' && verdict.message.trim() ? verdict.message.trim() : ''
          dispatch(
            onShowAlert(
              phaseCopy ||
                (copy
                  ? t(copy.key, { defaultValue: copy.defaultValue })
                  : serverMessage ||
                    t('worldMap.mvmReinforceUnavailable', {
                      defaultValue: 'Reinforcements are not available for this march right now.',
                    })),
              'info',
            ),
          )
        })
        .catch(() => {
          dispatch(
            onShowAlert(
              t('worldMap.mvmReinforceCheckFailed', {
                defaultValue: 'Could not check the battle status. Please try again.',
              }),
              'error',
            ),
          )
        })
    }
    window.addEventListener('mvm:open-reinforce', handler)
    return () => window.removeEventListener('mvm:open-reinforce', handler)
  }, [address, dispatch, t])

  /**
   * "Join attack" on a rally mail. The socket invite is a one-shot popup; this is the way back in. The
   * server verdict decides: joinable → the same rally panel the popup opens, with live figures; otherwise
   * the exact reason (the host already arrived, you already joined, the rally is full, ...).
   */
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = ((e as CustomEvent).detail || {}) as { attackId?: string }
      const attackId = detail?.attackId
      if (!attackId || !address) return
      getMvMRallyJoinEligibility(address, attackId)
        .then((verdict) => {
          if (verdict?.joinable) {
            setMvmRallyInvite({
              attackId,
              leaderName: verdict.leaderName ?? null,
              targetName: verdict.targetName ?? null,
              arrivesAt: verdict.arrivesAt ?? undefined,
              rallyPhaseEndAt: verdict.rallyPhaseEndAt ?? undefined,
              marchTravelTimeMs: typeof verdict.marchTravelTimeMs === 'number' ? verdict.marchTravelTimeMs : undefined,
              leaderCharacterCount:
                typeof verdict.leaderCharacterCount === 'number' ? verdict.leaderCharacterCount : undefined,
              rallyJoinMinBread: typeof verdict.rallyJoinMinBread === 'number' ? verdict.rallyJoinMinBread : undefined,
            })
            return
          }
          invalidateMvmRallyJoinAvailability(attackId)
          const RALLY_REASON_COPY: Record<string, { key: string; defaultValue: string }> = {
            phase_closed: {
              key: 'worldMap.mvmRallyPhaseClosed',
              defaultValue: 'The host has already reached the target — this rally no longer takes joiners.',
            },
            window_closed: {
              key: 'worldMap.mvmRallyWindowClosed',
              defaultValue: 'The gathering window for this rally has closed.',
            },
            already_joined: { key: 'worldMap.mvmRallyAlreadyJoined', defaultValue: 'You already joined this rally.' },
            cap_reached: { key: 'worldMap.mvmRallyFull', defaultValue: 'This rally already has the maximum number of joiners.' },
            not_in_alliance: {
              key: 'worldMap.mvmRallyNotInAlliance',
              defaultValue: 'You must be in the rally leader\u2019s alliance to join.',
            },
            is_leader: { key: 'worldMap.mvmRallyIsLeader', defaultValue: 'You lead this rally.' },
            is_defender: { key: 'worldMap.mvmRallyIsDefender', defaultValue: 'This rally is marching on your castle.' },
            support_tower_destroyed: {
              key: 'worldMap.mvmRallySupportTowerDown',
              defaultValue: 'Your side’s Support tower has fallen — the field can no longer receive allies.',
            },
            not_found: { key: 'worldMap.mvmRallyNotFound', defaultValue: 'This rally is no longer active.' },
          }
          const copy = RALLY_REASON_COPY[verdict?.reason || '']
          const serverMessage =
            typeof verdict?.message === 'string' && verdict.message.trim() ? verdict.message.trim() : ''
          dispatch(
            onShowAlert(
              copy
                ? t(copy.key, { defaultValue: copy.defaultValue })
                : serverMessage || t('worldMap.mvmJoinRallyFail', { defaultValue: 'Could not join this rally.' }),
              'info',
            ),
          )
        })
        .catch(() => {
          dispatch(
            onShowAlert(
              t('worldMap.mvmReinforceCheckFailed', {
                defaultValue: 'Could not check the battle status. Please try again.',
              }),
              'error',
            ),
          )
        })
    }
    window.addEventListener('mvm:open-rally', handler)
    return () => window.removeEventListener('mvm:open-rally', handler)
  }, [address, dispatch, t])

  // Skip showing alert on PVE pages, battle pages, all PVP pages, and Start page
  const isPVEPage = location.pathname.startsWith('/pve/')
  const isBattlePage = location.pathname.includes('/battlefield/') || location.pathname.includes('/battle-preview/')
  const isPVPPage = location.pathname.startsWith('/pvp')
  const isStartPage = location.pathname === '/start'
  const isHexBattlePage = location.pathname === '/hex-battle'
  const shouldHideAlert = isPVEPage || isBattlePage || isPVPPage || isStartPage || isHexBattlePage

  const prevShouldHideAlertRef = useRef<boolean | null>(null)
  useEffect(() => {
    const prev = prevShouldHideAlertRef.current
    prevShouldHideAlertRef.current = shouldHideAlert
    if (!walletConnectedForMainLand) return
    if (prev !== null && prev && !shouldHideAlert) {
      refetchMvmPendingRef.current?.()
    }
  }, [walletConnectedForMainLand, shouldHideAlert])

  // Timer: only re-render when displayed time string changes; remove expired attacks
  useEffect(() => {
    if (activeAttacks.size === 0) return

    const formatRemainingMs = (endMs?: number): string => {
      if (!endMs) return ''
      const remaining = Math.max(0, endMs - Date.now())
      const minutes = Math.floor(remaining / 60000)
      const seconds = Math.floor((remaining % 60000) / 1000)
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    }

    const interval = setInterval(() => {
      const attacks = activeAttacksRef.current
      const now = Date.now()
      const displayStr = Array.from(attacks.entries())
        .map(([_, a]) => {
          const eff = mvmAttackEndMsForUi(a, now)
          if (eff === Number.POSITIVE_INFINITY) return `hold:${a.attackId ?? ''}`
          return formatRemainingMs(eff)
        })
        .join('|')
      if (displayStr !== lastTimeDisplayRef.current) {
        lastTimeDisplayRef.current = displayStr
        setTimeDisplayKey((k) => k + 1)
      }
      setActiveAttacks((prev) => {
        const newMap = new Map(prev)
        let updated = false
        prev.forEach((attack, key) => {
          const eff = mvmAttackEndMsForUi(attack, now)
          if (eff != null && eff !== Number.POSITIVE_INFINITY && eff <= now) {
            newMap.delete(key)
            updated = true
          }
        })
        return updated ? newMap : prev
      })
      // Forget closed-banner keys that have expired so the map stays bounded.
      const dismissed = dismissedBannersUntilRef.current
      if (dismissed.size > 0) {
        for (const [dk, untilMs] of dismissed) {
          if (untilMs <= now) dismissed.delete(dk)
        }
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [activeAttacks.size])

  useEffect(() => {
    if (!address) {
      dispatch(clearMvMarchCharacterLocks())
      if (socketRef.current) {
        socketRef.current.removeAllListeners()
        socketRef.current.disconnect()
        socketRef.current = null
        socketWalletRef.current = null
      }
      registerMvMPendingSocket(null)
      setActiveAttacks(new Map())
      return
    }

    // Gate screen only: no barbarian/monster API checks and no attack socket until user leaves `/start`.
    if (!walletConnectedForMainLand) {
      if (socketRef.current) {
        socketRef.current.removeAllListeners()
        socketRef.current.disconnect()
        socketRef.current = null
        socketWalletRef.current = null
      }
      registerMvMPendingSocket(null)
      setActiveAttacks(new Map())
      return
    }

    const walletNorm = (address || '').toLowerCase().trim()
    if (socketRef.current && socketWalletRef.current !== walletNorm) {
      socketRef.current.removeAllListeners()
      socketRef.current.disconnect()
      socketRef.current = null
      socketWalletRef.current = null
    }

    if (!socketRef.current) {
      const apiKey = process.env.REACT_APP_X_API_KEY || ''
      const token = getAuthToken()
      const socket = io(config.socketURL as string, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        auth: {
          ...(token ? { token } : {}),
          ...(apiKey ? { 'x-api-key': apiKey } : {}),
        },
        extraHeaders: apiKey
          ? {
            'x-api-key': apiKey,
          }
          : {},
      })
      socketRef.current = socket
      socketWalletRef.current = walletNorm
    }
    const socket = socketRef.current
    registerMvMPendingSocket(socket)

    /**
     * Same-browser battle resume. Router state — the only place `battleId` used to live — dies with
     * the tab, so a player who closed the browser mid-battle had no way back in while their turn
     * timer kept running. If this browser recorded a battle for this wallet, confirm with the
     * server that it is still live and walk straight back into it.
     */
    const maybeResumeStoredMvMBattle = () => {
      if (mvmResumeCheckedRef.current || !address) return
      const stored = loadActiveMvMBattle(address)
      if (!stored) return
      mvmResumeCheckedRef.current = true
      // Never pull the player out of a fight they are already in (this or any other battle route).
      if (isOnBattlePath(locationPathRef.current)) return
      type ActiveBattleAck = { success?: boolean; active?: boolean; battleId?: string }
      socket.emit('mvm:get-active-battle', { walletAddress: address }, (res: ActiveBattleAck | null) => {
        // No answer / server error: keep the entry and retry on the next connect.
        if (!res?.success) {
          mvmResumeCheckedRef.current = false
          return
        }
        if (!res.active || String(res.battleId || '') !== stored.battleId) {
          clearActiveMvMBattle(stored.battleId)
          return
        }
        if (isOnBattlePath(locationPathRef.current)) return
        navigate('/hex-battle', {
          replace: true,
          state: { battleId: stored.battleId, ...(stored.mapType ? { mapType: stored.mapType } : {}) },
        })
      })
    }

    const subscribeToEvents = () => {
      if (socket.connected && address) {
        const walletNorm = (address || '').toLowerCase().trim()
        socket.emit('user:online', { walletAddress: address })
        socket.emit('join-wallet-room', { walletAddress: walletNorm })
        socket.emit('land:subscribe', { walletAddress: address })
        socket.emit('barbarians:subscribe', { walletAddress: address })
      }
    }

    // Subscribe immediately if already connected ('connect' will not fire again for this socket)
    if (socket.connected) {
      subscribeToEvents()
      maybeResumeStoredMvMBattle()
    }

    // Subscribe on initial connect
    socket.on('connect', async () => {
      subscribeToEvents()
      maybeResumeStoredMvMBattle()
      refetchBarbarianVillageDefenseAlerts()
      // Sync MvM pending state on every connect (initial + reconnect) so that if
      // a rally transitioned to march while the socket was offline or not yet in the
      // wallet room, the defender's incoming-attack banner appears immediately.
      refetchMvMPendingFromServer()

      // Check for active attacks when socket connects
      if (address) {
        // Check monster attacks for all castles
        for (let castleId = 1; castleId <= 4; castleId++) {
          try {
            const monsterStatus = await checkMonsterAttackStatus(address, castleId)
            if (monsterStatus.success && monsterStatus.isAttacking && monsterStatus.attackInfo) {
              const attackInfo = monsterStatus.attackInfo
              setActiveAttacks((prev) =>
                setAttackIfChanged(prev, `monster-castle${castleId}`, {
                  type: 'monster',
                  castleId: castleId,
                  targetBuilding: attackInfo.targetBuilding,
                  totalDamage: attackInfo.totalDamage,
                  startTime: normalizeTime(attackInfo.startTime),
                  endTime: normalizeTime(attackInfo.endTime),
                  walletAddress: address,
                }),
              )
            }
          } catch (error) {
            console.error(`Error checking monster attack status for Castle ${castleId}:`, error)
          }
        }
      }
    })

    socket.on('disconnect', (reason) => {
      console.log('GlobalAttackAlert: Socket disconnected:', reason)
    })

    // Monster attack events for all 4 castles
    for (let castleId = 1; castleId <= 4; castleId++) {
      socket.on(`monsters:castle${castleId}:attackStarted`, (data: any) => {
        if (data.walletAddress === address) {
          setActiveAttacks((prev) =>
            setAttackIfChanged(prev, `monster-castle${castleId}`, {
              type: 'monster',
              castleId: castleId,
              targetBuilding: data.targetBuilding,
              totalDamage: data.totalDamage,
              startTime: normalizeTime(data.startTime),
              endTime: normalizeTime(data.endTime),
              walletAddress: data.walletAddress,
            }),
          )
          dispatch(onShowAlert(t('modals.globalAttack.monstersAttackingCastleAlert', { castleId }), 'warning'))
        }
      })

      socket.on(`monsters:castle${castleId}:attackEnd`, (data: any) => {
        if (data.walletAddress === address) {
          setActiveAttacks((prev) => deleteAttackIfPresent(prev, `monster-castle${castleId}`))
        }
      })

      socket.on(`monsters:castle${castleId}:attackDefended`, (data: any) => {
        if (data.walletAddress === address) {
          setActiveAttacks((prev) => deleteAttackIfPresent(prev, `monster-castle${castleId}`))
          setRewardData({
            reward: data.reward || null,
            attackType: 'monster',
            castleId: castleId,
          })
          setShowRewardAlert(true)
        }
      })
    }

    const applyBarbarianVillageDefenseAlert = (data: any, opts?: { fromHydration?: boolean }) => {
      if (!data?.attackId || !address) return
      if (data.defenderWallet && normWallet(data.defenderWallet) !== normWallet(address)) return
      const vid = data.barbarianVillageId ? String(data.barbarianVillageId) : ''
      if (data.marchPhase === 'resolved') {
        if (vid) setActiveAttacks((prev) => deleteAttackIfPresent(prev, `mvm-bv_march_${vid}`))
        else if (data.attackId) setActiveAttacks((prev) => deleteAttackIfPresent(prev, `mvm-${data.attackId}`))
        setVillageDefense((cur) => (cur && cur.villageId === vid ? null : cur))
        return
      }
      if (data.marchPhase === 'arrived' && data.battleId) {
        setVillageDefense((cur) => (cur && cur.villageId === vid ? null : cur))
        if (vid) setActiveAttacks((prev) => deleteAttackIfPresent(prev, `mvm-bv_march_${vid}`))
        const bid = String(data.battleId).trim()
        if (bid) {
          const onHex = locationPathRef.current === '/hex-battle'
          const sameBattle =
            onHex &&
            hexBattleContextRef.current.battleId != null &&
            String(hexBattleContextRef.current.battleId) === bid
          if (!sameBattle) {
            navigate('/hex-battle', { replace: true, state: { battleId: bid } })
          }
        }
        return
      }
      const atkLabel = data.attackerUsername || t('worldMap.unknown')
      if (data.marchPhase === 'choose' || data.awaitingDefenderChoice) {
        setVillageDefense({
          worldId: data.barbarianWorldId || 'main',
          villageId: vid,
          attackerLabel: data.attackerUsername || undefined,
        })
        if (vid) setActiveAttacks((prev) => deleteAttackIfPresent(prev, `mvm-bv_march_${vid}`))
        if (!opts?.fromHydration) {
          dispatch(
            onShowAlert(
              t('worldMap.villageUnderAttackToast', {
                attacker: atkLabel,
                defaultValue: `${atkLabel} reached your barbarian village! Enter the battle to defend, or it resolves automatically.`,
              }),
              'warning',
            ),
          )
        }
        return
      }
      const arrivesAtMs = new Date(data.arrivesAt).getTime()
      const defenderW = (data.defenderWallet || address) as string
      // Owner closed this banner — suppress re-add until the 2-min dismiss window expires.
      const _vDk = villageBannerDismissKey(data.attackId, arrivesAtMs)
      const _vUntil = dismissedBannersUntilRef.current.get(_vDk)
      if (_vUntil && _vUntil > Date.now()) return
      setActiveAttacks((prev) =>
        setAttackIfChanged(prev, `mvm-${data.attackId}`, {
          type: 'mvm',
          defenseIncomingBanner: true,
          defenderWallet: defenderW,
          endTime: arrivesAtMs,
          startTime: Date.now(),
          walletAddress: address,
          attackId: data.attackId,
          attackerWallet: data.attackerWallet,
          attackerUsername: data.attackerUsername || undefined,
          mvmCountdownPhase: 'march',
          mvmPendingSnapshot: { status: 'traveling', arrivesAt: data.arrivesAt },
          mvmAttackType: 'barbarian_village',
          mvmStatusFromServer: 'traveling',
          toQ: typeof data.toQ === 'number' ? data.toQ : undefined,
          toR: typeof data.toR === 'number' ? data.toR : undefined,
        }),
      )
      if (!opts?.fromHydration) {
        dispatch(
          onShowAlert(
            t('worldMap.villageMarchIncomingToast', {
              attacker: atkLabel,
              defaultValue: `${atkLabel} is marching on your barbarian village.`,
            }),
            'warning',
          ),
        )
      }
    }

    const refetchBarbarianVillageDefenseAlerts = () => {
      if (!address) return
      getBarbarianVillageDefenseAlerts()
        .then((alerts) => {
          for (const alertRow of alerts) {
            applyBarbarianVillageDefenseAlert(alertRow, { fromHydration: true })
          }
        })
        .catch(() => {})
    }

    // MvM incoming attack (defender receives this when attacker starts marching)
    socket.on('mvm:incoming-attack', (data: any) => {
      if (!data?.attackId || !address) return
      if (data.defenderWallet && normWallet(data.defenderWallet) !== normWallet(address)) return
      if (data.attackType === 'barbarian_village') {
        applyBarbarianVillageDefenseAlert(data)
        if (data.marchPhase === 'choose' || data.awaitingDefenderChoice) {
          refetchBarbarianVillageDefenseAlerts()
        }
        return
      }
      const arrivesAtMs = new Date(data.arrivesAt).getTime()
      const _cDk = villageBannerDismissKey(data.attackId, arrivesAtMs)
      const _cUntil = dismissedBannersUntilRef.current.get(_cDk)
      if (_cUntil && _cUntil > Date.now()) return
      const defenderW = (data.defenderWallet || address) as string
      setActiveAttacks((prev) =>
        setAttackIfChanged(prev, `mvm-${data.attackId}`, {
          type: 'mvm',
          defenseIncomingBanner: true,
          defenderWallet: defenderW,
          endTime: arrivesAtMs,
          startTime: Date.now(),
          walletAddress: address,
          attackId: data.attackId,
          attackerWallet: data.attackerWallet,
          attackerUsername: data.attackerUsername || undefined,
          attackerAllianceName: data.attackerAllianceName || undefined,
          rallyMode: !!data.rallyMode,
          mvmCountdownPhase: 'march',
          mvmPendingSnapshot: { status: 'traveling', arrivesAt: data.arrivesAt },
          mvmAttackType: data.attackType === 'tower_pvp' ? 'tower_pvp' : 'castle',
          mvmStatusFromServer: 'traveling',
          defenderBusyInOtherBattle: data.attackType === 'tower_pvp' && !!data.defenderBusyInOtherBattle,
        }),
      )
      // Alert the defender with a toast so the incoming attack is unmissable.
      const attackerLabel = data.attackerUsername || t('worldMap.unknown')
      const allianceLabel = data.attackerAllianceName ? ` (${data.attackerAllianceName})` : ''
      const toastMsg =
        data.attackType === 'tower_pvp'
          ? data.defenderBusyInOtherBattle
            ? t('worldMap.mvmTowerIncomingToastBusy', {
              attacker: `${attackerLabel}${allianceLabel}`,
              defaultValue:
                '{{attacker}} started a march on your defense tower. You are in another battle — defending is optional; the tower will auto-resolve if you take no action.',
            })
            : t('worldMap.mvmTowerIncomingToast', {
              defaultValue:
                '{{attacker}} is marching on your defense tower. You may march to defend (optional) or ignore — a battle will still resolve automatically when they arrive.',
              attacker: `${attackerLabel}${allianceLabel}`,
            })
          : data.rallyMode
            ? t('worldMap.rallyMarchIncomingToast', { attacker: `${attackerLabel}${allianceLabel}` })
            : t('worldMap.mvmIncomingAttackToast', { attacker: `${attackerLabel}${allianceLabel}` })
      dispatch(onShowAlert(toastMsg, 'warning'))
      // Also surface it in the war ticker with the arrival time attached, so the alert carries a live
      // countdown ("arrives in 4:12") instead of only naming the attacker. `attackId` lets the ticker
      // follow later roster updates for this march; see GlobalNotificationToastHost.
      dispatch(
        addNotification({
          id: `mvm-incoming-${data.attackId}`,
          title: t('worldMap.mvmBanner.incomingTitle', { defaultValue: 'Incoming Attack' }),
          walletAddress: address || '',
          message: toastMsg,
          type: 'war',
          timestamp: Date.now(),
          read: false,
          metadata: {
            kind: 'mvm-incoming',
            attackId: data.attackId,
            arrivesAt: data.arrivesAt,
          },
        }),
      )
    })

    socket.on('mvm:castle-auto-defense-scheduled', (data: any) => {
      if (!data?.attackId || !data?.arrivesAt || !address) return
      if (data.defenderWallet && normWallet(data.defenderWallet) !== normWallet(address)) return
      dispatch(onShowAlert(t('worldMap.castleAutoDefenseScheduledToast'), 'info'))
      const arrivesAtMs = new Date(data.arrivesAt).getTime()
      const defenderW = (data.defenderWallet || address) as string
      setActiveAttacks((prev) =>
        setAttackIfChanged(prev, `mvm-${data.attackId}`, {
          type: 'mvm',
          defenseIncomingBanner: true,
          defenderWallet: defenderW,
          endTime: arrivesAtMs,
          startTime: Date.now(),
          walletAddress: address,
          attackId: data.attackId,
          attackerWallet: data.attackerWallet,
          attackerUsername: data.attackerUsername || undefined,
          rallyMode: !!data.rallyMode,
          mvmCountdownPhase: 'march',
          mvmPendingSnapshot: { status: 'traveling', arrivesAt: data.arrivesAt },
          mvmAttackType: data.attackType === 'tower_pvp' ? 'tower_pvp' : 'castle',
        }),
      )
    })

    socket.on('mvm:castle-auto-defense-started', (data: any) => {
      if (!data?.attackId) return
      dispatch(onShowAlert(t('worldMap.castleAutoDefenseStartedToast'), 'info'))
      if (defenderAttackIdRef.current === data.attackId) {
        setDefenderChoiceOpen(false)
        setDefenderManualModalOpen(false)
        setDefenderAttackId(null)
        defenderPickingCharactersRef.current = null
      }
    })

    // MvM attack arrived (both sides receive this — castle flow only; tower_pvp uses evaluateTowerPvPContestAfterAttackerArrived)
    socket.on('mvm:attack-arrived', (data: any) => {
      if (!data?.attackId || !address) return
      const me = normWallet(address)
      const attackerW = data.attackerWallet ? normWallet(data.attackerWallet) : ''
      const defenderW = data.defenderWallet ? normWallet(data.defenderWallet) : ''
      if (attackerW === me && defenderW !== me && data.attackType !== 'tower_pvp') {
        /**
         * Attacker feedback is the map sword FX, not a toast — this event can arrive twice (busy-auto
         * re-emit) and the world map runs its own arrival handshake, so a toast here stacked N times.
         * The world map resolves the hex from `attackId` and de-dupes an already-showing overlay.
         */
        /**
         * `awaitingFirstReinforcement`: the castle owner is offline but an ally is still marching in, so
         * the server holds the siege at the gate until they land instead of auto-resolving. Without the
         * sword FX the attacker's map would look idle for the whole hold — the siege IS running.
         */
        if (data.awaitingDefenderChoice === true || data.awaitingFirstReinforcement === true) {
          dispatchWorldMapMvmAutoSword(true, { attackId: data.attackId, attackType: 'castle' })
        }
      }
      if (defenderW !== me) return
      if (data.attackerBusyAutoDefense) {
        dispatch(onShowAlert(t('worldMap.castleAutoDefenseStartedToast'), 'info'))
        setActiveAttacks((prev) => deleteAttackIfPresent(prev, `mvm-${data.attackId}`))
        return
      }
      const isTowerAttack = data.attackType === 'tower_pvp'
      if (shouldOfferDefenderChoiceRef.current(data.attackId)) {
        setDefenderMvmAttackType(isTowerAttack ? 'tower_pvp' : 'castle')
        setDefenderAttackId(data.attackId)
        setDefenderChoiceOpen(true)
      }
      if (!isTowerAttack) {
        setActiveAttacks((prev) => deleteAttackIfPresent(prev, `mvm-${data.attackId}`))
      }
      /** If path/duplicate-event blocked the modal, REST still reflects character_selection. */
      getMvMPendingAttacks(address)
        .then((result) => {
          const me = normWallet(address)
          const attacks = result?.attacks || []
          const charSel = attacks.find(
            (atk: {
              defenderWallet?: string
              status?: string
              attackId?: string
              attackType?: string
              defenderAttackerBusyAutoDefense?: boolean
              arrivesAt?: string
              startedAt?: string
              rallyPhaseEndAt?: string
              marchTravelTimeMs?: number
              travelTimeMs?: number
              rallyJoiners?: MvMPendingLike['rallyJoiners']
            }) =>
              normWallet(atk.defenderWallet) === me &&
              atk.status === 'character_selection' &&
              atk.attackType !== 'tower_pvp' &&
              !atk.defenderAttackerBusyAutoDefense &&
              atk.attackId === data.attackId,
          )
          const snap: MvMPendingLike | null = charSel
            ? {
              status: charSel.status,
              arrivesAt: charSel.arrivesAt,
              startedAt: (charSel as { startedAt?: string }).startedAt,
              rallyPhaseEndAt: charSel.rallyPhaseEndAt,
              marchTravelTimeMs: charSel.marchTravelTimeMs,
              travelTimeMs: charSel.travelTimeMs,
              rallyJoiners: charSel.rallyJoiners,
            }
            : null
          if (charSel?.attackId && shouldOfferDefenderChoiceRef.current(charSel.attackId)) {
            setDefenderMvmAttackType('castle')
            setDefenderAttackId(charSel.attackId)
            setDefenderChoiceOpen(true)
          }
        })
        .catch(() => { })
    })

    /** World-map PvE (barbarian / neutral tower): server finished auto-battle while client was off the map. */
    socket.on('world:pve-march:auto-resolved', (data: any) => {
      if (!address) return
      // On `/world-map` the page owns the result modal (sword timing + dedupe with the HTTP path).
      if (locationPathRef.current === '/world-map') return
      const kind = data?.kind === 'defense_tower' ? 'defense_tower' : 'barbarian'
      const outcome = String(data?.battleOutcome || '')
      const won = outcome === 'attacker_wins' || outcome.startsWith('attacker_wins')
      const resultRaw =
        data?.result && typeof data.result === 'object' ? (data.result as AutoBattleResult) : null
      const result =
        resultRaw && resultRaw.battleOutcome
          ? resultRaw
          : outcome
            ? ({ ...(resultRaw || {}), battleOutcome: outcome } as AutoBattleResult)
            : null
      if (result && result.battleOutcome) {
        setWorldMapPveResultModal({ result, type: kind })
      }
      if (kind === 'defense_tower') {
        dispatch(
          onShowAlert(
            won
              ? t('worldMap.pveTowerMarchFinishedWin', {
                  defaultValue: 'Your defense tower raid finished — victory.',
                })
              : t('worldMap.pveTowerMarchFinishedLoss', {
                  defaultValue: 'Your defense tower raid finished — the garrison held.',
                }),
            won ? 'success' : 'info',
          ),
        )
      } else {
        dispatch(
          onShowAlert(
            won
              ? t('worldMap.pveBarbarianMarchFinishedWin', {
                  defaultValue: 'Your barbarian raid finished — victory.',
                })
              : t('worldMap.pveBarbarianMarchFinishedLoss', {
                  defaultValue: 'Your barbarian raid finished — defeat.',
                }),
            won ? 'success' : 'info',
          ),
        )
      }
    })

    // MvM battle started — leader often already on hex-battle; allies get a clear modal + countdown
    socket.on('mvm:battle-started', (data: any) => {
      if (!data?.battleId) return
      if (data.attackId) {
        dispatch(removeMvMPendingAttack(data.attackId))
      }
      setActiveAttacks((prev) => {
        const newMap = new Map(prev)
        for (const key of prev.keys()) {
          if (key.startsWith('mvm-')) newMap.delete(key)
        }
        return newMap
      })
      const role = data.participantRole as string | undefined
      if (
        role === 'defender_owner' &&
        address &&
        data.attackId &&
        normWallet(data.defenderWallet) === normWallet(address)
      ) {
        defenderBattleEnteredRef.current.add(data.attackId)
        defenderPickingCharactersRef.current = null
        setDefenderChoiceOpen(false)
        setDefenderManualModalOpen(false)
        setDefenderAttackId(null)
        setDefenderMvmAttackType('castle')
      }
      const mapType = normalizeServerMapType(String(data.mapType))
      if (locationPathRef.current === '/hex-battle') {
        window.dispatchEvent(new CustomEvent(MVM_BATTLE_STARTED_EVENT, { detail: { ...data, mapType } }))
        return
      }
      if (role === 'attacker_ally' || role === 'defender_ally') {
        dispatch(
          onShowAlert(
            role === 'attacker_ally' ? t('worldMap.mvmBattleLiveAttackerAlly') : t('worldMap.mvmBattleLiveDefenderAlly'),
            'info',
          ),
        )
        navigate('/hex-battle', { replace: true, state: { battleId: data.battleId, mapType } })
        return
      }
      navigate('/hex-battle', { replace: true, state: { battleId: data.battleId, mapType } })
    })

    /**
     * Owned-village hex defence: attacker is navigated from WorldMapPage; the defender may be on main land
     * or any route — mirror the attacker fallback here so Enter Battle always lands in the hex even if the
     * HTTP response is slow or lost after the server already built the battle.
     */
    socket.on('world:pve-march:hex-battle-started', (data: { battleId?: string }) => {
      const bid = data?.battleId != null ? String(data.battleId).trim() : ''
      if (!bid) return
      const onHex = locationPathRef.current === '/hex-battle'
      const sameBattle =
        onHex &&
        hexBattleContextRef.current.battleId != null &&
        String(hexBattleContextRef.current.battleId) === bid
      if (!sameBattle) {
        navigate('/hex-battle', { replace: true, state: { battleId: bid } })
      }
    })

    /**
     * Sent to both alliances the moment a siege's battle is over (the row turned `returning`). Allies who
     * never joined are in no battle room and get no `mvm:battle-ended`; this is what takes their mail
     * buttons down at once instead of at the next re-check.
     */
    socket.on('mvm:siege-ended', (data: { attackId?: string } | null) => {
      const aid = data?.attackId !== null && data?.attackId !== undefined ? String(data.attackId).trim() : ''
      if (!aid) return
      invalidateMvmReinforceAvailability(aid)
      invalidateMvmRallyJoinAvailability(aid)
    })

    socket.on('mvm:battle-ended', (data: any) => {
      // Battles also end while the player is elsewhere (offline resolve, auto-battle) — drop the
      // resume pointer here too, not only when the hex battle page happens to be open.
      if (data?.battleId) clearActiveMvMBattle(String(data.battleId))
      // The incoming-attack ticker line has served its purpose once the battle resolves.
      if (data?.attackId) clearMarchNotifications(String(data.attackId))
      // Dismiss the owned-village "Enter/Auto" prompt once that village's battle resolves.
      if (data?.barbarianVillageId) {
        const vid = String(data.barbarianVillageId)
        setVillageDefense((cur) => (cur && cur.villageId === vid ? null : cur))
      }
      /**
       * Always schedule a HUD clear attempt: castle offline resolve always includes battleId, but we also match
       * attackId so the banner never sticks if payload shape differs. Defer one frame so auto-progress state commits first.
       */
      const bidRaw = data?.battleId
      const aidRaw = data?.attackId ?? data?.pendingAttackId
      const marchUnlockAid =
        aidRaw != null && String(aidRaw).trim() ? String(aidRaw).trim() : ''
      if (marchUnlockAid) {
        dispatch(unlockMvMarchCharactersForAttack(marchUnlockAid))
      }
      window.setTimeout(() => {
        setMvmAutoBattleProgressHud((prev) => {
          if (!prev) return prev
          const bid = bidRaw != null ? String(bidRaw) : null
          const aid = aidRaw != null ? String(aidRaw) : null
          if (bid != null && String(prev.battleId) === bid) return null
          if (aid != null && prev.attackId != null && String(prev.attackId) === aid) return null
          return prev
        })
      }, 0)

      const endAttackIdRaw = data.attackId ?? data.pendingAttackId
      const endAttackId =
        endAttackIdRaw != null && String(endAttackIdRaw).trim() ? String(endAttackIdRaw).trim() : ''

      /**
       * Retire the finished march for EVERY participant, before the outcome-modal gate below.
       *
       * `showGlobalOutcome` asks "should this player see a win/lose modal?", which is only ever true
       * for the attacker or the defender (`data.you` is one of those two). A reinforcing ally is
       * neither, so they used to return early and keep a pending row for a battle that was already
       * over — their champions went on marching to the castle. Clearing the row is about the march
       * being finished, not about who gets a modal, so it belongs on this side of the gate.
       */
      if (endAttackId) {
        dispatch(removeMvMPendingAttack(endAttackId))
        defenderBattleEnteredRef.current.add(endAttackId)
        setActiveAttacks((prev) => deleteAttackIfPresent(prev, `mvm-${endAttackId}`))
        // The mail buttons ("Help defend" / "Join attack") exist only while the siege is live.
        invalidateMvmReinforceAvailability(endAttackId)
        invalidateMvmRallyJoinAvailability(endAttackId)
        scheduleRefetchMvMPendingDebounced()
        /**
         * The march mail ("they arrive in 15 seconds") is resolved server-side on read, so refetch
         * it now that the siege is over — otherwise a mailbox left open keeps showing a finished
         * attack as a live threat until the player reloads.
         */
        if (address) dispatch(fetchNotifications(address) as any)
      }

      const showGlobalOutcome =
        data?.autoResolvedWithoutHex === true ||
        data?.hexBattleCompleted === true ||
        (typeof data?.won === 'boolean' && (data?.you === 'attacker' || data?.you === 'defender'))

      if (!showGlobalOutcome) return
      setDefenderChoiceOpen(false)
      setDefenderManualModalOpen(false)
      setDefenderAttackId(null)
      defenderPickingCharactersRef.current = null

      const hexCtx = hexBattleContextRef.current
      const onSameHexPage =
        hexCtx.path === '/hex-battle' &&
        data?.battleId != null &&
        hexCtx.battleId != null &&
        String(data.battleId) === String(hexCtx.battleId)

      const hexFinishedUi =
        data.hexBattleCompleted === true ||
        (typeof data.won === 'boolean' &&
          data.autoResolvedWithoutHex !== true &&
          (data.you === 'attacker' || data.you === 'defender'))

      if (hexFinishedUi && onSameHexPage) {
        scheduleRefetchMvMPendingDebounced()
        return
      }

      const towerPvP = data.attackType === 'tower_pvp'
      const you = data.you === 'defender' ? 'defender' as const : 'attacker' as const;
      if (typeof data.won === 'boolean') {
        const endAttackIdForModal =
          endAttackId ||
          (data.attackId ?? data.pendingAttackId) != null
            ? String(data.attackId ?? data.pendingAttackId).trim()
            : ''
        scheduleMvmAutoResolveModal({
          won: data.won,
          you,
          message: String(data.message || '').trim(),
          characterXp: data.won
            ? Number(data.characterXpWinner ?? 0) || 0
            : Number(data.characterXpLoser ?? 0) || 0,
          userXp: data.won ? Number(data.userXpWinner ?? 0) || 0 : Number(data.userXpLoser ?? 0) || 0,
          attackId: endAttackIdForModal,
          battleId: data.battleId != null ? String(data.battleId).trim() : undefined,
          showResourceLoot:
            !towerPvP &&
            data.you === 'attacker' &&
            data.won === true &&
            data.resourcesSeized === true,
          seizedResources:
            !towerPvP &&
            data.you === 'attacker' &&
            data.won === true &&
            data.resourcesSeized === true &&
            data.seizedResources &&
            typeof data.seizedResources === 'object'
              ? (data.seizedResources as Record<string, number>)
              : null,
          attackType: String(data.attackType || 'castle'),
          allianceTaskBattle: data.allianceTaskBattle === true,
        })
        if (data.autoResolvedWithoutHex === true && !towerPvP && endAttackIdForModal && address) {
          window.setTimeout(() => {
            void mvmAttackArrived(address, endAttackIdForModal)
              .then((d) =>
                dispatchReturnMarchFromApi(d && typeof d === 'object' ? (d as Record<string, unknown>) : null),
              )
              .catch(() => {})
          }, 400)
        }
        scheduleRefetchMvMPendingDebounced()
        return
      }

      if (data.message) {
        dispatch(onShowAlert(mvmAutoResolveBattleToast(String(data.message), t), 'info'))
      }
    })

    socket.on('mvm:auto-battle-progress', (data: any) => {
      if (!address || !data?.battleId) return
      /** Wallet-room banner only; hex page owns live PvP UI (and `location.state.battleId` may be missing after refresh). */
      if (locationPathRef.current === '/hex-battle') return
      const me = normWallet(address)
      const attW = data.attackerWallet ? normWallet(data.attackerWallet) : ''
      const defW = data.defenderWallet ? normWallet(data.defenderWallet) : ''
      const partList: string[] = Array.isArray(data.participantWallets)
        ? data.participantWallets.map((w: string) => normWallet(w)).filter(Boolean)
        : []
      const inParticipantList = partList.length > 0 && partList.some((w) => w === me)
      if (!inParticipantList && me !== attW && me !== defW) return

      const kind = String(data.kind || '')
      const round = typeof data.roundNumber === 'number' ? data.roundNumber : Number(data.roundNumber) || 1
      const attackType = String(data.attackType || 'castle')

      let line = ''
      if (kind === 'auto_battle_started') {
        line = t('mvm.autoBattleRemote.bannerStarted', {
          round,
          defaultValue:
            'Calculating the battle on the server — comparing armies. This usually takes just a moment.',
        })
      } else if (kind === 'auto_battle_resolved') {
        line = t('mvm.autoBattleRemote.bannerResolved', {
          defaultValue: 'Battle decided — opening your result…',
        })
      } else if (kind === 'defender_ai_turn' || kind === 'attacker_ai_turn' || kind === 'hex_turn') {
        const tr = buildMvMTurnResultFromAutoProgressPayload(data)
        const summary = tr ? formatMvMTurnSummary(tr) : ''
        line = summary
          ? t('mvm.autoBattleRemote.bannerTurn', {
            round,
            summary,
            defaultValue: 'Round {{round}} — {{summary}}',
          })
          : t('mvm.autoBattleRemote.bannerWaiting', {
            round,
            defaultValue: 'Round {{round}} — resolving…',
          })
      } else {
        line = t('mvm.autoBattleRemote.bannerUpdate', {
          round,
          defaultValue: 'Round {{round}} — battle update.',
        })
      }

      setMvmAutoBattleProgressHud((prev) => {
        const next = {
          battleId: String(data.battleId),
          attackId:
            data.attackId != null
              ? String(data.attackId)
              : data.pendingAttackId != null
                ? String(data.pendingAttackId)
                : prev?.battleId === String(data.battleId)
                  ? prev.attackId
                  : null,
          round,
          line,
          attackType,
        }
        return autoBattleHudEqual(prev, next) ? prev : next
      })
    })

    socket.on('mvm:offense-roster-updated', (data: any) => {
      if (data?.attackId) dispatch(mergeOffenseRoster(data))
    })

    socket.on('mvm:defense-roster-updated', (data: any) => {
      if (data?.attackId) dispatch(mergeDefenseRoster(data))
    })

    socket.on('mvm:rally-started', (data: any) => {
      if (!data?.attackId || !address) return
      if (normWallet(data.leaderWallet) === normWallet(address)) return
      setMvmRallyInvite({
        attackId: data.attackId,
        leaderName: data.leaderName,
        targetName: data.targetName,
        arrivesAt: data.arrivesAt,
        rallyPhaseEndAt: typeof data.rallyPhaseEndAt === 'string' ? data.rallyPhaseEndAt : undefined,
        marchTravelTimeMs: typeof data.marchTravelTimeMs === 'number' ? data.marchTravelTimeMs : undefined,
        leaderCharacterCount: typeof data.leaderCharacterCount === 'number' ? data.leaderCharacterCount : undefined,
        rallyJoinMinBread: typeof data.rallyJoinMinBread === 'number' ? data.rallyJoinMinBread : undefined,
      })
      dispatch(onShowAlert(t('worldMap.mvmRallyInviteToast'), 'info'))
    })

    socket.on('mvm:rally-march-started', (data: any) => {
      if (!data?.attackId || !data.arrivesAt || !address) return
      const me = normWallet(address)
      const defW = data.defenderWallet ? normWallet(data.defenderWallet) : ''
      const reinf: string[] = Array.isArray(data.defenderReinforcementWallets) ? data.defenderReinforcementWallets : []
      const showDefenseIncoming =
        (!!defW && defW === me) || reinf.some((w) => normWallet(w) === me)

      // Defender / reinforcer: update or create the incoming-march banner immediately.
      if (showDefenseIncoming) {
        const marchEnd = new Date(data.arrivesAt).getTime()
        const defenderWalletRaw = (data.defenderWallet || '') as string
        setActiveAttacks((prev) => {
          const k = `mvm-${data.attackId}`
          const cur = prev.get(k)
          if (cur?.type === 'mvm' && cur.defenseIncomingBanner) {
            return setAttackIfChanged(prev, k, {
              ...cur,
              endTime: marchEnd,
              rallyMode: true,
              mvmCountdownPhase: 'march',
              mvmPendingSnapshot: { status: 'traveling', arrivesAt: data.arrivesAt },
              defenderWallet: cur.defenderWallet || defenderWalletRaw || undefined,
              attackerWallet: data.attackerWallet ?? cur.attackerWallet,
            })
          }
          /** Defender / reinforcer had no gather-phase HUD; march start may be the first banner row. */
          return setAttackIfChanged(prev, k, {
            type: 'mvm',
            defenseIncomingBanner: true,
            defenderWallet: defenderWalletRaw || undefined,
            endTime: marchEnd,
            startTime: Date.now(),
            walletAddress: address,
            attackId: data.attackId,
            attackerWallet: data.attackerWallet,
            rallyMode: true,
            mvmCountdownPhase: 'march',
            mvmPendingSnapshot: { status: 'traveling', arrivesAt: data.arrivesAt },
            mvmAttackType: data.attackType === 'tower_pvp' ? 'tower_pvp' : 'castle',
            mvmStatusFromServer: 'traveling',
          })
        })
      }

      // All participants (leader, joiners, defender, reinforcers): sync roster state
      // and ensure the world-map panel / defense banner reflect the new traveling status.
      // scheduleRefetchMvMPendingDebounced is defined later; handler runs async after init.
      scheduleRefetchMvMPendingDebounced()
    })

    socket.on('mvm:defender-tower-march', (data: any) => {
      if (!data?.attackId || !address) return
      const me = normWallet(address)
      const defW = data.defenderWallet ? normWallet(data.defenderWallet) : ''
      const attW = data.attackerWallet ? normWallet(data.attackerWallet) : ''
      if (me !== defW && me !== attW) return
      scheduleRefetchMvMPendingDebounced()
    })

    /** Tower PvP: defender reached the tower hex while attacker is still marching — server scheduler + socket. */
    socket.on('mvm:tower-defender-holding', (data: any) => {
      if (!data?.attackId || !address) return
      const me = normWallet(address)
      const defW = data.defenderWallet ? normWallet(data.defenderWallet) : ''
      const attW = data.attackerWallet ? normWallet(data.attackerWallet) : ''
      scheduleRefetchMvMPendingDebounced()
      if (defW === me) {
        dispatch(
          onShowAlert(
            t('worldMap.mvmTowerDefenderHoldingToast', {
              defaultValue: 'Your champions hold the tower — waiting for the attacker to arrive.',
            }),
            'info',
          ),
        )
      } else if (attW === me) {
        dispatch(
          onShowAlert(
            t('worldMap.mvmTowerAttackerOpponentHoldingToast', {
              defaultValue: 'The defender reached the tower first. Battle starts when your march completes.',
            }),
            'info',
          ),
        )
      }
    })

    /** Tower PvP: server auto-resolve starting (offline power or forfeit) — show persistent banner until `mvm:battle-ended`. */
    socket.on('mvm:tower-pvp-auto-battle', (data: any) => {
      if (!data?.attackId || !address) return
      const me = normWallet(address)
      const defW = data.defenderWallet ? normWallet(data.defenderWallet) : ''
      const attW = data.attackerWallet ? normWallet(data.attackerWallet) : ''
      if (me !== defW && me !== attW) return
      scheduleRefetchMvMPendingDebounced()
      const toQ = typeof data.toQ === 'number' ? data.toQ : Number(data.toQ)
      const toR = typeof data.toR === 'number' ? data.toR : Number(data.toR)
      if (data?.battleId) {
        setMvmAutoBattleProgressHud({
          battleId: String(data.battleId),
          attackId: String(data.attackId),
          round: 1,
          line: t('mvm.autoBattleRemote.bannerTowerOffline'),
          attackType: 'tower_pvp',
        })
      }
      if (Number.isFinite(toQ) && Number.isFinite(toR)) {
        dispatchWorldMapMvmAutoSword(true, {
          attackId: String(data.attackId),
          battleId: data.battleId != null ? String(data.battleId) : null,
          attackType: 'tower_pvp',
          targetQ: toQ,
          targetR: toR,
        })
      }
    })

    socket.on('mvm:attacker-return-march', (data: any) => {
      if (!data?.attackId || !data.returnArrivesAt || !address) return
      const me = normWallet(address)
      const atkW = data.attackerWallet ? normWallet(data.attackerWallet) : ''
      if (atkW !== me) return
      const returnEnd = new Date(data.returnArrivesAt).getTime()
      setActiveAttacks((prev) => {
        const base = deleteAttackIfPresent(prev, `mvm-${data.attackId}`)
        return setAttackIfChanged(base, `mvm-return-${data.attackId}`, {
          type: 'mvm',
          attackerReturningBanner: true,
          endTime: returnEnd,
          startTime: Date.now(),
          walletAddress: address,
          attackId: data.attackId,
          attackerWallet: data.attackerWallet,
          mvmCountdownPhase: 'march',
          mvmPendingSnapshot: { status: 'returning', returnArrivesAt: data.returnArrivesAt },
        })
      })
      fetchCastleOutcomeForReturningMarch(String(data.attackId), address)
      scheduleRefetchMvMPendingDebounced()
      // Homecoming moved off the top banner plate into the war ticker, where the same countdown
      // machinery renders "Homecoming in 3:08" (see GlobalNotificationToastHost).
      dispatch(
        addNotification({
          id: `mvm-return-${data.attackId}`,
          title: t('worldMap.mvmBanner.returningTitle'),
          walletAddress: address || '',
          message: t('worldMap.mvmBanner.returningTitle'),
          type: 'war',
          timestamp: Date.now(),
          read: false,
          metadata: {
            kind: 'mvm-returning',
            attackId: data.attackId,
            arrivesAt: data.returnArrivesAt,
          },
        }),
      )
    })

    socket.on('mvm:defender-return-march', (data: any) => {
      if (!data?.attackId || !address) return
      const me = normWallet(address)
      const defW = data.defenderWallet ? normWallet(data.defenderWallet) : ''
      if (defW !== me) return
      const marchMsSock = typeof data.marchMs === 'number' ? data.marchMs : null
      const returnEndEarly = data.returnArrivesAt ? new Date(data.returnArrivesAt).getTime() : NaN
      const instantRecall =
        marchMsSock === 0 || (Number.isFinite(returnEndEarly) && returnEndEarly <= Date.now() + 400)
      if (instantRecall) {
        setActiveAttacks((prev) => deleteAttackIfPresent(prev, `mvm-def-return-${data.attackId}`))
        scheduleRefetchMvMPendingDebounced()
        return
      }
      if (!data.returnArrivesAt) return
      const returnEnd = new Date(data.returnArrivesAt).getTime()
      setActiveAttacks((prev) => {
        let base = deleteAttackIfPresent(prev, `mvm-${data.attackId}`)
        base = deleteAttackIfPresent(base, `mvm-def-return-${data.attackId}`)
        return setAttackIfChanged(base, `mvm-def-return-${data.attackId}`, {
          type: 'mvm',
          defenderReturningBanner: true,
          endTime: returnEnd,
          startTime: Date.now(),
          walletAddress: address,
          attackId: data.attackId,
          defenderWallet: data.defenderWallet,
          mvmAttackType: 'tower_pvp',
          mvmCountdownPhase: 'march',
          mvmPendingSnapshot: { status: 'returning', returnArrivesAt: data.returnArrivesAt },
        })
      })
      scheduleRefetchMvMPendingDebounced()
      dispatch(
        addNotification({
          id: `mvm-def-return-${data.attackId}`,
          title: t('worldMap.mvmBanner.defenderReturningTitle'),
          walletAddress: address || '',
          message: t('worldMap.mvmBanner.defenderReturningTitle'),
          type: 'war',
          timestamp: Date.now(),
          read: false,
          metadata: {
            kind: 'mvm-returning',
            attackId: data.attackId,
            arrivesAt: data.returnArrivesAt,
          },
        }),
      )
    })

    socket.on('mvm:attack-cancelled', (data: any) => {
      if (!data?.attackId) return
      defenderBattleEnteredRef.current.delete(data.attackId)
      if (defenderPickingCharactersRef.current === data.attackId) {
        defenderPickingCharactersRef.current = null
      }
      dispatch(removeMvMPendingAttack(data.attackId))
      dispatch(unlockMvMarchCharactersForAttack(data.attackId))
      clearMarchNotifications(data.attackId)
      setActiveAttacks((prev) => deleteAttackIfPresent(deleteAttackIfPresent(prev, `mvm-${data.attackId}`), `mvm-def-return-${data.attackId}`))
      setMvmRallyInvite((inv) => (inv?.attackId === data.attackId ? null : inv))
      setMvmReinforceInvite((inv) => (inv?.attackId === data.attackId ? null : inv))

      const reason = data?.reason as string | undefined
      const toQ = typeof data?.toQ === 'number' ? data.toQ : Number(data?.toQ)
      const toR = typeof data?.toR === 'number' ? data.toR : Number(data?.toR)
      if (Number.isFinite(toQ) && Number.isFinite(toR)) {
        dispatchMvmMapClear({ attackId: data.attackId, toQ, toR })
      }

      const me = normWallet(address)
      const leaderNorm = normWallet(data?.attackerWallet as string | undefined)
      const defenderNorm = normWallet(data?.defenderWallet as string | undefined)

      let msg = t('worldMap.mvmAttackCancelledGenericToast')
      if (reason === 'rally_no_joiners') {
        if (defenderNorm && defenderNorm === me) {
          msg = ''
        } else if (leaderNorm && leaderNorm === me) {
          msg = t('worldMap.rallyCancelledNoJoinersToast')
        } else {
          msg = t('worldMap.rallyCancelledJoinerToast')
        }
      } else if (reason === 'attacker_withdrew') {
        msg =
          defenderNorm && defenderNorm === me
            ? t('worldMap.enemyRecalledMarchToast')
            : t('worldMap.marchRecalledToast')
      } else if (reason === 'defender_shielded') {
        msg =
          defenderNorm && defenderNorm === me
            ? t('worldMap.attackCancelledByYourShieldToast')
            : t('worldMap.marchCancelledDefenderShieldedToast')
      }

      if (msg) {
        void alert.open(msg)
        dispatch(onShowAlert(msg, 'info'))
      }
    })

    socket.on('mvm:reinforcement-call', (data: any) => {
      if (!data?.attackId || !address) return
      if (normWallet(data.defenderWallet) === normWallet(address)) return
      setMvmReinforceInvite({
        attackId: data.attackId,
        message: typeof data.message === 'string' ? data.message : undefined,
        arrivesAt: data.arrivesAt,
        helpRequestedByDefender: !!data.helpRequestedByDefender,
        defenderWallet: typeof data.defenderWallet === 'string' ? data.defenderWallet : undefined,
        worldId: typeof data.worldId === 'string' ? data.worldId : undefined,
      })
      dispatch(onShowAlert(t('worldMap.mvmReinforcementCallToast'), 'info'))
    })

    let mvmPendingRefetchDebounceTimer: number | null = null
    const MVM_PENDING_REFETCH_DEBOUNCE_MS = 260

    const refetchMvMPendingFromServer = () => {
      if (!address) return
      getMvMPendingRosters(address)
        .then((rosterRes: { attacks?: unknown[] }) => {
          const arr = rosterRes?.attacks
          if (Array.isArray(arr)) {
            dispatch(setMvMPendingRostersFromServer(arr as MvMPendingAttackEntry[]))
          }
        })
        .catch(() => { })

      getMvMPendingAttacks(address)
        .then((result) => {
          const me = normWallet(address)
          const attacks = result?.attacks || []
          /** Only the castle owner and committed reinforcers see the red "incoming attack" march banner. */
          const userShouldSeeDefenseIncomingBanner = (atk: {
            defenderWallet?: string
            defenderReinforcements?: { walletAddress?: string }[]
          }) => {
            if (normWallet(atk.defenderWallet) === me) return true
            if ((atk.defenderReinforcements || []).some((r) => normWallet(r.walletAddress) === me)) return true
            return false
          }
          /* Hoisted out of the updater below so the notification retirement after it can read the
             same server truth; React may call an updater twice in StrictMode, so these are filled
             idempotently (Set.add) and never accumulate across calls. */
          const marchIds = new Set<string>()
          const returnIds = new Set<string>()
          const defenderReturnIds = new Set<string>()
          setActiveAttacks((prev) => {
            const newMap = new Map(prev)
            for (const atk of attacks) {
              const st = String(atk.status || '')

              if (st === 'returning' && atk.attackId) {
                if (normWallet(atk.attackerWallet) === me) {
                  const retAt = (atk as { returnArrivesAt?: string }).returnArrivesAt
                  if (retAt) {
                    returnIds.add(atk.attackId)
                    const returnEnd = new Date(retAt).getTime()
                    if (returnEnd > Date.now()) {
                      newMap.set(`mvm-return-${atk.attackId}`, {
                        type: 'mvm',
                        attackerReturningBanner: true,
                        endTime: returnEnd,
                        startTime: Date.now(),
                        walletAddress: address,
                        attackId: atk.attackId,
                        attackerWallet: atk.attackerWallet,
                        mvmCountdownPhase: 'march',
                        mvmPendingSnapshot: { status: 'returning', returnArrivesAt: retAt },
                      })
                      fetchCastleOutcomeForReturningMarch(atk.attackId, address)
                    }
                  }
                }
                const defRetAt = (atk as { defenderReturnArrivesAt?: string }).defenderReturnArrivesAt
                if (
                  (atk as { attackType?: string }).attackType === 'tower_pvp' &&
                  normWallet(atk.defenderWallet) === me &&
                  defRetAt
                ) {
                  const defReturnEnd = new Date(defRetAt).getTime()
                  if (defReturnEnd > Date.now()) {
                    defenderReturnIds.add(atk.attackId)
                    newMap.set(`mvm-def-return-${atk.attackId}`, {
                      type: 'mvm',
                      defenderReturningBanner: true,
                      endTime: defReturnEnd,
                      startTime: Date.now(),
                      walletAddress: address,
                      attackId: atk.attackId,
                      defenderWallet: atk.defenderWallet,
                      attackerWallet: atk.attackerWallet,
                      mvmAttackType: 'tower_pvp',
                      mvmCountdownPhase: 'march',
                      mvmPendingSnapshot: { status: 'returning', returnArrivesAt: defRetAt },
                    })
                  }
                }
                continue
              }

              const isTower = (atk as { attackType?: string }).attackType === 'tower_pvp'
              const defIsMe = normWallet(atk.defenderWallet) === me
              const allowArrivedBanner = isTower && st === 'arrived' && defIsMe
              if (!['traveling', 'rallying'].includes(st) && !allowArrivedBanner) continue
              if (!userShouldSeeDefenseIncomingBanner(atk)) continue
              if (!atk.attackId || !atk.arrivesAt) continue
              marchIds.add(atk.attackId)
              const like: MvMPendingLike = {
                status: st,
                arrivesAt: atk.arrivesAt,
                startedAt: (atk as { startedAt?: string }).startedAt,
                rallyPhaseEndAt: (atk as { rallyPhaseEndAt?: string }).rallyPhaseEndAt,
                marchTravelTimeMs: (atk as { marchTravelTimeMs?: number }).marchTravelTimeMs,
                travelTimeMs: (atk as { travelTimeMs?: number }).travelTimeMs,
                rallyJoiners: (atk as { rallyJoiners?: MvMPendingLike['rallyJoiners'] }).rallyJoiners,
              }
              const endMs = mvmPendingArrivalEndMs(like)
              const phase = mvmPendingDisplayPhase(like)
              const existingEntry = newMap.get(`mvm-${atk.attackId}`)
              const _rDk = villageBannerDismissKey(atk.attackId, endMs)
              const _rUntil = dismissedBannersUntilRef.current.get(_rDk)
              if (_rUntil && _rUntil > Date.now()) continue
              const defArr = (atk as { defenderArrivesAt?: string }).defenderArrivesAt
              const towerMarchCommitted = !!(defArr && new Date(defArr).getTime() > 0)
              newMap.set(`mvm-${atk.attackId}`, {
                type: 'mvm',
                defenseIncomingBanner: true,
                defenderWallet: atk.defenderWallet,
                endTime: endMs,
                startTime: atk.startedAt ? new Date(atk.startedAt).getTime() : Date.now(),
                walletAddress: address,
                attackId: atk.attackId,
                attackerWallet: atk.attackerWallet,
                attackerUsername:
                  (atk as { attackerUsername?: string }).attackerUsername ||
                  existingEntry?.attackerUsername ||
                  undefined,
                attackerAllianceName:
                  (atk as { attackerAllianceName?: string }).attackerAllianceName ||
                  existingEntry?.attackerAllianceName ||
                  undefined,
                rallyMode: !!atk.rallyMode,
                mvmCountdownPhase: phase,
                mvmPendingSnapshot: like,
                mvmAttackType:
                  (atk as { attackType?: string }).attackType === 'tower_pvp'
                    ? 'tower_pvp'
                    : existingEntry?.mvmAttackType === 'tower_pvp'
                      ? 'tower_pvp'
                      : 'castle',
                towerMarchCommitted,
                defenderArrivesAtIso: typeof defArr === 'string' ? defArr : undefined,
                mvmStatusFromServer: st,
                defenderBusyInOtherBattle: existingEntry?.defenderBusyInOtherBattle === true,
                defenderAttackerBusyAutoDefense: !!(atk as { defenderAttackerBusyAutoDefense?: boolean })
                  .defenderAttackerBusyAutoDefense,
              })
            }
            for (const key of newMap.keys()) {
              if (key.startsWith('mvm-return-')) {
                const entry = newMap.get(key) as AttackInfo | undefined
                if (entry?.attackId && !returnIds.has(entry.attackId)) {
                  newMap.delete(key)
                }
                continue
              }
              if (key.startsWith('mvm-def-return-')) {
                const entry = newMap.get(key) as AttackInfo | undefined
                if (entry?.attackId && !defenderReturnIds.has(entry.attackId)) {
                  newMap.delete(key)
                }
                continue
              }
              if (!key.startsWith('mvm-')) continue
              const entry = newMap.get(key) as AttackInfo | undefined
              if (entry?.type !== 'mvm' || !entry.attackId) continue
              if (normWallet(entry.walletAddress) !== me) continue
              if (!entry.defenseIncomingBanner) {
                newMap.delete(key)
                continue
              }
              if (!marchIds.has(entry.attackId)) {
                // Barbarian-village MARCH banners are socket-driven and have NO PendingAttack row to refetch
                // (the march is tracked on the village doc, not a PendingAttack), so their attackId is never
                // in marchIds. Keep them: they self-retire on arrival (the 'resolved'/battleId incoming-attack
                // emit deletes this entry) or when the arrival time passes (timer cleanup). Without this, the
                // 10s refetch wiped the owner's incoming-village banner seconds after it appeared.
                const isVillageMarchBanner =
                  entry.mvmAttackType === 'barbarian_village' && entry.mvmStatusFromServer === 'traveling'
                if (!isVillageMarchBanner) {
                  newMap.delete(key)
                }
              }
            }
            return attackMapShallowEqual(prev, newMap) ? prev : newMap
          })
          /**
           * Retire the march ticker lines for anything the server no longer reports as in flight.
           *
           * `clearMarchNotifications` alone is not enough. On the castle path the server emits
           * `mvm:battle-ended` FIRST and only schedules the return march afterwards (see the
           * `finally` block in mvmBattleOutcome.handleBattleEnd), so the clear ran BEFORE
           * `mvm:attacker-return-march` re-added the row — and nothing retires it when the army
           * actually gets home. Combined with the reducer's 24h preserve rule for client-only
           * rows, every attack left a permanent "Campaign closed — your host rides for home" in
           * the mailbox. This runs off the same server truth the banner map above uses, so a row
           * disappears the moment its attack stops being `returning`.
           */
          for (const n of notificationsRef.current) {
            const nid = n?.id || n?._id || ''
            if (!nid) continue
            const aid =
              typeof n?.metadata?.attackId === 'string' ? n.metadata.attackId : ''
            if (!aid) continue
            if (nid.startsWith('mvm-return-') && !returnIds.has(aid)) {
              dispatch(removeNotification(nid))
            } else if (nid.startsWith('mvm-def-return-') && !defenderReturnIds.has(aid)) {
              dispatch(removeNotification(nid))
            } else if (nid.startsWith('mvm-incoming-') && !marchIds.has(aid)) {
              dispatch(removeNotification(nid))
            }
          }
          const charSel = attacks.find(
            (atk: {
              defenderWallet?: string
              status?: string
              attackId?: string
              attackType?: string
              defenderAttackerBusyAutoDefense?: boolean
              arrivesAt?: string
              startedAt?: string
              rallyPhaseEndAt?: string
              marchTravelTimeMs?: number
              travelTimeMs?: number
              rallyJoiners?: MvMPendingLike['rallyJoiners']
            }) =>
              normWallet(atk.defenderWallet) === me &&
              atk.status === 'character_selection' &&
              atk.attackType !== 'tower_pvp' &&
              !atk.defenderAttackerBusyAutoDefense,
          )
          const pendingLike: MvMPendingLike | null = charSel
            ? {
              status: charSel.status,
              arrivesAt: charSel.arrivesAt,
              startedAt: (charSel as { startedAt?: string }).startedAt,
              rallyPhaseEndAt: charSel.rallyPhaseEndAt,
              marchTravelTimeMs: charSel.marchTravelTimeMs,
              travelTimeMs: charSel.travelTimeMs,
              rallyJoiners: charSel.rallyJoiners,
            }
            : null
          if (charSel?.attackId && shouldOfferDefenderChoiceRef.current(charSel.attackId)) {
            setDefenderMvmAttackType('castle')
            setDefenderAttackId(charSel.attackId)
            setDefenderChoiceOpen(true)
          }
        })
        .catch(() => { })
    }

    const scheduleRefetchMvMPendingDebounced = () => {
      if (mvmPendingRefetchDebounceTimer != null) window.clearTimeout(mvmPendingRefetchDebounceTimer)
      mvmPendingRefetchDebounceTimer = window.setTimeout(() => {
        mvmPendingRefetchDebounceTimer = null
        refetchMvMPendingFromServer()
      }, MVM_PENDING_REFETCH_DEBOUNCE_MS)
    }

    refetchMvmPendingRef.current = refetchMvMPendingFromServer

    /**
     * Drop the ticker alerts for a march that is over.
     *
     * The incoming-attack line has no auto-dismiss on purpose — a raid warning must not expire while
     * the player is looking away — so once the attack actually resolves (cancelled, arrived, battle
     * ended) something has to retire it, or the player is left with a permanent "Arriving now" line
     * for a battle that already finished. Ids are deterministic, so this clears exactly the two lines
     * this attack could have produced and nothing else.
     */
    const clearMarchNotifications = (attackId: string) => {
      if (!attackId) return
      dispatch(removeNotification(`mvm-incoming-${attackId}`))
      dispatch(removeNotification(`mvm-return-${attackId}`))
      dispatch(removeNotification(`mvm-def-return-${attackId}`))
    }

    socket.on('mvm:pending-refresh', () => {
      scheduleRefetchMvMPendingDebounced()
    })

    socket.on('reconnect', () => {
      subscribeToEvents()
      refetchBarbarianVillageDefenseAlerts()
      refetchMvMPendingFromServer()
    })

    refetchBarbarianVillageDefenseAlerts()
    refetchMvMPendingFromServer()

    /**
     * Defensive periodic poll so the defender's "Enter battle vs Auto battle" modal is
     * guaranteed to appear within ~30 s of a pending attack reaching `character_selection`,
     * even if every realtime trigger fails:
     *  - `mvm:attack-arrived` socket event missed during a brief disconnect.
     *  - `mvm:pending-refresh` socket event also missed in the same window.
     *  - `socket.on('connect')` already fired with no pending data at that moment.
     *  - User on a `shouldHideAlert` page while the events fire — covered separately by the
     *    page-transition refetch, but a fast tab-switch could still slip between them.
     *
     * No-ops while the socket is disconnected: each tick calls `refetchMvMPendingFromServer`
     * which is a single small REST call to `/mvm/pending-attacks?walletAddress=...`. The
     * inner `getMvMPendingAttacks(address)` is unconditional, so we don't need the socket
     * to be connected for this safety net to work — it survives socket flaps entirely.
     */
    // Ransom request received (owner side): another player wants to release a captured character for resources
    socket.on('mvm:ransom-request', (data: any) => {
      if (!data?.ransomId) return
      setRansomRequests(prev => {
        if (prev.some(r => r.ransomId === data.ransomId)) return prev
        return [...prev, {
          ransomId: String(data.ransomId),
          characterInstanceId: String(data.characterInstanceId || ''),
          characterName: String(data.characterName || ''),
          characterImg: String(data.characterImg || ''),
          holderWallet: String(data.holderWallet || ''),
          holderNick: String(data.holderNick || data.holderWallet || ''),
          resources: (data.resources || {}) as RansomResources,
          resourceSummary: String(data.resourceSummary || ''),
        }]
      })
      const nick = String(data.holderNick || data.holderWallet || 'An enemy')
      const name = String(data.characterName || 'your character')
      const summary = String(data.resourceSummary || '')
      dispatch(addNotification({
        id: `ransom-request-${data.ransomId}`,
        title: 'Ransom Request',
        walletAddress: address || '',
        message: summary
          ? `${nick} offers to release ${name} for: ${summary}.`
          : `${nick} sent a ransom request for ${name}.`,
        type: 'war',
        timestamp: Date.now(),
        read: false,
      }))
      // Also fetch any queued pending ransoms from DB on reconnect
      if (address) void getPendingRansomRequestsApi(address).then((res: any) => {
        const list = Array.isArray(res?.data) ? res.data : []
        if (list.length === 0) return
        setRansomRequests(prev => {
          const existing = new Set(prev.map(r => r.ransomId))
          const fresh = list
            .filter((d: any) => !existing.has(String(d._id)))
            .map((d: any) => ({
              ransomId: String(d._id),
              characterInstanceId: String(d.characterInstanceId || ''),
              characterName: String(d.characterName || ''),
              characterImg: String(d.characterImg || ''),
              holderWallet: String(d.holderWallet || ''),
              holderNick: String(d.holderWallet || ''),
              resources: (d.resources || {}) as RansomResources,
              resourceSummary: '',
            }))
          return [...prev, ...fresh]
        })
      }).catch(() => {})
    })

    // Prisoner action notification (owner side): holder recruited, beheaded, or released their character
    socket.on('mvm:prisoner-action', (data: any) => {
      const name = data?.characterName || 'Your character'
      const nick = data?.holderNick || 'An enemy'
      const ts = Date.now()
      if (data?.action === 'recruited') {
        const msg = `${name} was recruited into ${nick}'s army.`
        dispatch(onShowAlert(msg, 'warning'))
        dispatch(addNotification({
          id: `prisoner-recruited-${ts}`,
          title: 'Prisoner Recruited',
          walletAddress: address || '',
          message: msg,
          type: 'war',
          timestamp: ts,
          read: false,
        }))
      } else if (data?.action === 'beheaded') {
        const msg = `${name} was executed by ${nick}.`
        dispatch(onShowAlert(msg, 'error'))
        dispatch(addNotification({
          id: `prisoner-beheaded-${ts}`,
          title: 'Prisoner Executed',
          walletAddress: address || '',
          message: msg,
          type: 'war',
          timestamp: ts,
          read: false,
        }))
      } else if (data?.action === 'released') {
        const msg = `${name} was released by ${nick} and has returned to you.`
        dispatch(onShowAlert(msg, 'success'))
        dispatch(addNotification({
          id: `prisoner-released-${ts}`,
          title: 'Prisoner Released',
          walletAddress: address || '',
          message: msg,
          type: 'war',
          timestamp: ts,
          read: false,
        }))
      }
    })

    // Ransom response received (holder side): owner accepted or declined
    socket.on('mvm:ransom-response', (data: any) => {
      if (!data?.ransomId) return
      const ts = Date.now()
      if (data.accepted) {
        const resText = Object.entries((data.resources || {}) as Record<string, number>)
          .filter(([, v]) => v > 0)
          .map(([k, v]) => `${v} ${k}`)
          .join(', ')
        const msg = `${data.characterName || 'Prisoner'} released — received ${resText || 'ransom'}.`
        dispatch(onShowAlert(msg, 'success'))
        dispatch(addNotification({
          id: `ransom-accepted-${data.ransomId}`,
          title: 'Ransom Accepted',
          walletAddress: address || '',
          message: msg,
          type: 'war',
          timestamp: ts,
          read: false,
        }))
        // Refresh captives list immediately on the holder's side
        window.dispatchEvent(new CustomEvent('eclipsera:mvm-captives-refresh'))
        // Refresh holder's resources (ransom payment received)
        if (address) dispatch(getResources(address, null, () => {}) as any)
      } else {
        const msg = `${data.characterName || 'Prisoner'}: ransom request declined.`
        dispatch(onShowAlert(msg, 'warning'))
        dispatch(addNotification({
          id: `ransom-declined-${data.ransomId}`,
          title: 'Ransom Declined',
          walletAddress: address || '',
          message: msg,
          type: 'war',
          timestamp: ts,
          read: false,
        }))
      }
    })

    const MVM_PENDING_POLL_MS = 30_000
    const pollHandle = window.setInterval(() => {
      refetchBarbarianVillageDefenseAlerts()
      refetchMvMPendingFromServer()
    }, MVM_PENDING_POLL_MS)

    return () => {
      if (mvmPendingRefetchDebounceTimer != null) window.clearTimeout(mvmPendingRefetchDebounceTimer)
      window.clearInterval(pollHandle)
      refetchMvmPendingRef.current = null
      socket.off('connect')
      socket.off('reconnect')
      socket.off('disconnect')
      socket.off('mvm:incoming-attack')
      socket.off('mvm:castle-auto-defense-scheduled')
      socket.off('mvm:castle-auto-defense-started')
      socket.off('mvm:attack-arrived')
      socket.off('world:pve-march:auto-resolved')
      socket.off('world:pve-march:hex-battle-started')
      socket.off('mvm:battle-started')
      socket.off('mvm:battle-ended')
      socket.off('mvm:siege-ended')
      socket.off('mvm:offense-roster-updated')
      socket.off('mvm:defense-roster-updated')
      socket.off('mvm:rally-started')
      socket.off('mvm:rally-march-started')
      socket.off('mvm:defender-tower-march')
      socket.off('mvm:tower-defender-holding')
      socket.off('mvm:tower-pvp-auto-battle')
      socket.off('mvm:auto-battle-progress')
      socket.off('mvm:attacker-return-march')
      socket.off('mvm:defender-return-march')
      socket.off('mvm:attack-cancelled')
      socket.off('mvm:reinforcement-call')
      socket.off('mvm:pending-refresh')
      socket.off('mvm:ransom-request')
      socket.off('mvm:ransom-response')
      socket.off('mvm:prisoner-action')
      for (let castleId = 1; castleId <= 4; castleId++) {
        socket.off(`monsters:castle${castleId}:attackStarted`)
        socket.off(`monsters:castle${castleId}:attackEnd`)
        socket.off(`monsters:castle${castleId}:attackDefended`)
      }
      // Detach listeners only — keep the socket session alive across in-app route changes.
      // Wallet/`/start` teardown is handled in the effect's early-return branches above.
    }
  }, [address, walletConnectedForMainLand])

  const handleDefend = async (attackKey: string, attackInfo: AttackInfo) => {
    if (!address || isDefending.get(attackKey)) return

    if (attackInfo.type === 'mvm' && attackInfo.attackId) {
      return
    }

    setIsDefending((prev) => {
      const newMap = new Map(prev)
      newMap.set(attackKey, true)
      return newMap
    })

    try {
      let res
      if (attackInfo.type === 'monster' && attackInfo.castleId) {
        res = await preventMonsterAttack(address, attackInfo.castleId)
      } else {
        return
      }

      if (res.success === true) {
        // Get reward from various possible response formats
        const reward = res.reward !== undefined ? res.reward : res.data?.reward

        // Show reward alert component
        setRewardData({
          reward: reward || null,
          attackType: attackInfo.type,
          castleId: attackInfo.castleId,
        })
        setShowRewardAlert(true)

        // Remove attack from active attacks
        setActiveAttacks((prev) => {
          const newMap = new Map(prev)
          newMap.delete(attackKey)
          return newMap
        })
      } else {
        dispatch(onShowAlert(res.message || t('modals.globalAttack.failedToDefend'), 'error'))
      }
    } catch (error) {
      console.error('Error defending attack:', error)
      dispatch(onShowAlert(t('modals.globalAttack.errorDefending'), 'error'))
    } finally {
      setIsDefending((prev) => {
        const newMap = new Map(prev)
        newMap.set(attackKey, false)
        return newMap
      })
    }
  }

  const getTimeRemaining = (attack?: AttackInfo): string => {
    if (!attack) return ''
    const now = Date.now()
    const endMs = mvmAttackEndMsForUi(attack, now)
    if (endMs === Number.POSITIVE_INFINITY) {
      return t('worldMap.mvmBanner.waitingAttackerLine', { defaultValue: 'Waiting for the attacker…' })
    }
    if (!endMs) return ''
    const remaining = Math.max(0, endMs - now)
    const minutes = Math.floor(remaining / 60000)
    const seconds = Math.floor((remaining % 60000) / 1000)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const allAttacks = useMemo(() => Array.from(activeAttacks.entries()), [activeAttacks])
  const mvmAttacks = useMemo(
    () =>
      allAttacks.filter(
        ([_, a]) =>
          a.type === 'mvm' &&
          (a.defenseIncomingBanner || a.attackerReturningBanner || a.defenderReturningBanner),
      ),
    [allAttacks],
  )
  const nonMvmAttacks = useMemo(() => allAttacks.filter(([_, a]) => a.type !== 'mvm'), [allAttacks])

  /**
   * Owner-facing barbarian-village incoming-march banners, soonest arrival first. A player can own many
   * villages and several can be under attack at once (one attacker each — enforced server-side), which
   * otherwise renders as a stack of identical "village under attack" lines.
   */
  const villageIncomingBanners = useMemo(() => {
    const me = address ? normWallet(address) : ''
    if (!me) return [] as Array<[string, AttackInfo]>
    return mvmAttacks
      .filter(
        ([, a]) =>
          a.defenseIncomingBanner &&
          a.mvmAttackType === 'barbarian_village' &&
          !!a.defenderWallet &&
          normWallet(a.defenderWallet) === me,
      )
      .sort(([, a], [, b]) => (normalizeTime(a.endTime) ?? Infinity) - (normalizeTime(b.endTime) ?? Infinity))
  }, [mvmAttacks, address])

  /** Collapse those village banners to ONE (the soonest); everything else (tower/castle/return) is untouched. */
  const renderedMvmAttacks = useMemo(() => {
    if (villageIncomingBanners.length <= 1) return mvmAttacks
    const dropKeys = new Set(villageIncomingBanners.slice(1).map(([key]) => key))
    return mvmAttacks.filter(([key]) => !dropKeys.has(key))
  }, [mvmAttacks, villageIncomingBanners])

  const hiddenVillageAttackCount = Math.max(0, villageIncomingBanners.length - 1)

  /** Close (X) an incoming-attack banner — hides it now and re-shows after 2 minutes. */
  const dismissBanner = (atk: AttackInfo) => {
    if (!atk.attackId) return
    const key = villageBannerDismissKey(atk.attackId, normalizeTime(atk.endTime))
    dismissedBannersUntilRef.current.set(key, Date.now() + 2 * 60 * 1000)
    setActiveAttacks((prev) => deleteAttackIfPresent(prev, `mvm-${atk.attackId}`))
  }

  const shouldShowAttackModal = nonMvmAttacks.length > 0 && !shouldHideAlert
  // Driven by the filtered list so suppressing incoming-attack plates leaves no empty banner shell.
  const shouldShowMvmBanner = renderedMvmAttacks.length > 0 && !shouldHideAlert && !mvmAutoBattleProgressHud
  /**
   * Top-centre MvM banner plate is retired for BOTH kinds it used to render:
   *  - incoming attacks, which the escalated `war` line in GlobalNotificationToastHost now announces
   *    with a live countdown (rendering both gave one raid two competing alerts);
   *  - returning armies ("your host rides for home"), whose march is already shown on the world map.
   * Flip to `shouldShowMvmBanner || showAutoBattleProgressBanner` to bring the plate back.
   */
  const SHOW_MVM_TOP_BANNER_PLATE = false
  /**
   * Auto-battle HUD is for wallet rooms only. Always hide on `/hex-battle` — the hex scene shows rounds;
   * matching `battleId` via `location.state` breaks after refresh/direct navigation and left the banner stuck.
   */
  /** Banner hidden — world map shows sword animation on defender hex instead (see {@link WORLD_MAP_MVM_AUTO_SWORD_EVENT}). */
  const showAutoBattleProgressBanner = false
  const showMvmTopBannerStack = SHOW_MVM_TOP_BANNER_PLATE && (shouldShowMvmBanner || showAutoBattleProgressBanner)

  /** Tower PvP: defender reached the tower first — park the alert at left / vertical middle (not top center). */
  const towerHoldWaitingAttackerLayout = useMemo(() => {
    if (!shouldShowMvmBanner || !address) return false
    const me = normWallet(address)
    const now = Date.now()
    for (const [, atk] of mvmAttacks) {
      if (atk.type !== 'mvm') continue
      if (atk.mvmAttackType !== 'tower_pvp') continue
      if (!atk.defenseIncomingBanner) continue
      if (!atk.defenderWallet || normWallet(atk.defenderWallet) !== me) continue
      if (towerDefenderHoldUntilAttackerResolves(atk, now)) return true
    }
    return false
  }, [shouldShowMvmBanner, mvmAttacks, address])

  const hasBarbarianAttack = nonMvmAttacks.some(([_, attack]) => attack.type === 'barbarian')
  const isBarbarian = hasBarbarianAttack
  const cornerColors = isBarbarian
    ? {
      topLeft: 'rgba(239, 68, 68, 0.6)',
      topRight: 'rgba(220, 38, 38, 0.6)',
      bottomLeft: 'rgba(185, 28, 28, 0.6)',
      bottomRight: 'rgba(239, 68, 68, 0.6)',
    }
    : {
      topLeft: 'rgba(139, 69, 19, 0.6)',
      topRight: 'rgba(101, 67, 33, 0.6)',
      bottomLeft: 'rgba(120, 53, 15, 0.6)',
      bottomRight: 'rgba(139, 69, 19, 0.6)',
    }

  const accentColor = isBarbarian ? 'rgba(239, 68, 68, 0.3)' : 'rgba(139, 69, 19, 0.3)'

  const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '95vw',
    maxWidth: '750px',
    maxHeight: '95vh',
    outline: 'none',
    backgroundColor: 'transparent',
    padding: '0.5rem',
  }

  return (
    <>
      {/* Scrollbar styles */}
      <style>{`
        .global-attack-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .global-attack-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 4px;
        }
        .global-attack-scrollbar::-webkit-scrollbar-thumb {
          background: ${isBarbarian ? 'rgba(239, 68, 68, 0.5)' : 'rgba(139, 69, 19, 0.5)'};
          border-radius: 4px;
        }
        .global-attack-scrollbar::-webkit-scrollbar-thumb:hover {
          background: ${isBarbarian ? 'rgba(239, 68, 68, 0.7)' : 'rgba(139, 69, 19, 0.7)'};
        }
        @keyframes mvmAutoBattleBannerPulse {
          0%, 100% { box-shadow: 0 4px 24px rgba(80,60,160,0.35), 0 0 0 1px rgba(255,255,255,0.06) inset; border-color: rgba(180,140,255,0.45); }
          50% { box-shadow: 0 4px 28px rgba(120,90,220,0.5), 0 0 0 1px rgba(255,255,255,0.1) inset; border-color: rgba(200,170,255,0.65); }
        }
      `}</style>

      {!shouldHideAlert && mvmAllyDeploymentHint && (
        <div
          className="fixed left-1/2 bottom-4 z-[10010] flex max-w-lg -translate-x-1/2 items-start gap-3 rounded-lg border border-amber-700/50 bg-stone-950/92 px-4 py-3 shadow-xl backdrop-blur-sm"
          style={{ fontFamily: 'Tilt Warp, sans-serif' }}
          role="status"
        >
          <div className="min-w-0">
            <p className="text-xs font-bold text-amber-100/95">
              {mvmAllyDeploymentHint.kind === 'rally' ? 'Alliance march — waiting for battle' : 'Castle reinforcement — waiting for battle'}
            </p>
            <p className="mt-1 text-[11px] leading-snug text-stone-300/95">
              {mvmAllyDeploymentHint.kind === 'rally'
                ? `You are deployed with ${mvmAllyDeploymentHint.leaderName}. When the hex battle opens, you will be taken to the battlefield automatically if you are online and available.`
                : `You are reinforcing ${mvmAllyDeploymentHint.castleLabel}. When the hex battle opens, you will be taken to the battlefield automatically if you are online and available.`}
            </p>
          </div>
          <button
            type="button"
            aria-label={t('common.close', { defaultValue: 'Close' })}
            title={t('common.close', { defaultValue: 'Close' })}
            onClick={() => dismissAllyDeploymentHint(mvmAllyDeploymentHint.attackId)}
            style={{
              flexShrink: 0,
              alignSelf: 'flex-start',
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '7px',
              border: '1px solid rgba(200,160,70,0.25)',
              background: 'rgba(8,5,2,0.6)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              color: 'rgba(230,205,155,0.75)',
              cursor: 'pointer',
              padding: 0,
              boxShadow: 'inset 0 1px 0 rgba(255,220,120,0.06)',
            }}
          >
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
              <line x1="1.5" y1="1.5" x2="9.5" y2="9.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              <line x1="9.5" y1="1.5" x2="1.5" y2="9.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}

      {shouldShowAttackModal && (
        <TailwindModal
          open={true}
          onClose={() => { }}
          aria-labelledby="attack-alert-modal-title"
          aria-describedby="attack-alert-modal-description"
          disableEscapeKeyDown={true}
        >
          <div style={modalStyle}>
            <div
              className="relative w-full backdrop-blur-sm rounded-xl sm:rounded-2xl flex flex-col overflow-hidden"
              style={{
                background: isBarbarian
                  ? `
                radial-gradient(ellipse 150% 120% at 0% 0%, rgba(239, 68, 68, 0.3), transparent 70%),
                radial-gradient(ellipse 150% 120% at 100% 100%, rgba(220, 38, 38, 0.25), transparent 70%),
                radial-gradient(circle at 50% 50%, rgba(185, 28, 28, 0.4), transparent),
                linear-gradient(135deg, rgba(127, 29, 29, 0.95) 0%, rgba(153, 27, 27, 0.9) 50%, rgba(127, 29, 29, 0.95) 100%)
              `
                  : `
                radial-gradient(ellipse 150% 120% at 0% 0%, rgba(139, 69, 19, 0.3), transparent 70%),
                radial-gradient(ellipse 150% 120% at 100% 100%, rgba(101, 67, 33, 0.25), transparent 70%),
                radial-gradient(circle at 50% 50%, rgba(120, 53, 15, 0.4), transparent),
                linear-gradient(135deg, rgba(69, 26, 3, 0.95) 0%, rgba(101, 67, 33, 0.9) 50%, rgba(69, 26, 3, 0.95) 100%)
              `,
                border: `2px solid ${isBarbarian ? 'rgba(239, 68, 68, 0.5)' : 'rgba(139, 69, 19, 0.5)'}`,
                boxShadow: `
              0 20px 60px rgba(0, 0, 0, 0.8),
              0 0 0 1px ${accentColor},
              inset 0 1px 0 rgba(255, 255, 255, 0.1),
              inset 0 -1px 0 rgba(0, 0, 0, 0.3)
            `,
                minHeight: '280px',
                maxHeight: '90vh',
              }}
            >
              <div
                className="absolute top-0 left-0 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 border-t-2 border-l-2 rounded-tl-xl sm:rounded-tl-2xl"
                style={{ borderColor: cornerColors.topLeft }}
              />
              <div
                className="absolute top-0 right-0 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 border-t-2 border-r-2 rounded-tr-xl sm:rounded-tr-2xl"
                style={{ borderColor: cornerColors.topRight }}
              />
              <div
                className="absolute bottom-0 left-0 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 border-b-2 border-l-2 rounded-bl-xl sm:rounded-bl-2xl"
                style={{ borderColor: cornerColors.bottomLeft }}
              />
              <div
                className="absolute bottom-0 right-0 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 border-b-2 border-r-2 rounded-br-xl sm:rounded-br-2xl"
                style={{ borderColor: cornerColors.bottomRight }}
              />

              <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6 md:p-8 gap-4 sm:gap-5 md:gap-6">
                <div className="flex flex-col items-center gap-3 sm:gap-4 w-full">
                  <h2
                    id="attack-alert-modal-title"
                    className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white text-center uppercase tracking-wider px-2 sm:px-4"
                    style={{
                      fontFamily: 'Tilt Warp',
                      textShadow: '0 4px 8px rgba(0, 0, 0, 0.8), 0 0 20px rgba(255, 255, 255, 0.3)',
                      lineHeight: '1.2',
                    }}
                  >
                    {allAttacks.length === 1
                      ? allAttacks[0][1].type === 'barbarian'
                        ? t('modals.globalAttack.barbariansAttacking')
                        : t('modals.globalAttack.monstersAttackingCastle', { castleId: allAttacks[0][1].castleId })
                      : t('modals.globalAttack.multipleAttacks')}
                  </h2>

                  <div
                    className={`w-full flex flex-col gap-3 sm:gap-4 ${allAttacks.length > 2 ? 'global-attack-scrollbar overflow-y-auto pr-1 sm:pr-2' : 'overflow-hidden'} ${allAttacks.length > 2 ? 'max-h-[50vh] sm:max-h-[55vh] md:max-h-[60vh]' : ''}`}
                    style={allAttacks.length > 2 ? {
                      scrollbarWidth: 'thin',
                      scrollbarColor: `${isBarbarian ? 'rgba(239, 68, 68, 0.5)' : 'rgba(139, 69, 19, 0.5)'} rgba(0, 0, 0, 0.3)`,
                    } : {}}
                  >
                    {nonMvmAttacks.map(([attackKey, attackInfo]) => {
                      const defending = isDefending.get(attackKey) || false
                      const timeRemaining = getTimeRemaining(attackInfo)
                      const isAttackBarbarian = attackInfo.type === 'barbarian'

                      return (
                        <div
                          key={attackKey}
                          className="flex flex-col gap-3 sm:gap-4 p-3 sm:p-4 md:p-5 rounded-lg sm:rounded-xl transition-all hover:scale-[1.01]"
                          style={{
                            background: `linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.4))`,
                            border: `2px solid ${isAttackBarbarian ? 'rgba(239, 68, 68, 0.5)' : 'rgba(139, 69, 19, 0.5)'}`,
                            boxShadow: `
                          0 4px 12px rgba(0, 0, 0, 0.4),
                          inset 0 1px 0 rgba(255, 255, 255, 0.05),
                          inset 0 -1px 0 rgba(0, 0, 0, 0.2)
                        `,
                          }}
                        >
                          {/* Attack Info */}
                          <div className="flex flex-col items-center gap-2 sm:gap-3">
                            <div
                              className="text-base sm:text-lg md:text-xl text-white font-bold text-center px-2"
                              style={{
                                fontFamily: 'Tilt Warp',
                                textShadow: '0 2px 4px rgba(0, 0, 0, 0.8), 0 0 8px rgba(255, 255, 255, 0.1)',
                                lineHeight: '1.3',
                              }}
                            >
                              {attackInfo.type === 'barbarian'
                                ? t('modals.globalAttack.barbariansAttackingMainLand')
                                : t('modals.globalAttack.monstersAttackingCastle', { castleId: attackInfo.castleId })}
                            </div>

                            {/* Info Row */}
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 w-full">
                              {/* Time Remaining */}
                              {timeRemaining && (
                                <div
                                  className="text-sm sm:text-base md:text-lg text-white/90 font-medium px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg whitespace-nowrap"
                                  style={{
                                    fontFamily: 'Tilt Warp',
                                    background: `linear-gradient(135deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.4))`,
                                    border: `1px solid ${isAttackBarbarian ? 'rgba(239, 68, 68, 0.3)' : 'rgba(139, 69, 19, 0.3)'}`,
                                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)',
                                    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.3)',
                                  }}
                                >
                                  {t('modals.globalAttack.time')}: {timeRemaining}
                                </div>
                              )}

                              {/* Target Building Info */}
                              {attackInfo.targetBuilding && (
                                <div
                                  className="text-xs sm:text-sm md:text-base text-white/80 font-medium px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg whitespace-nowrap"
                                  style={{
                                    fontFamily: 'Tilt Warp',
                                    background: `linear-gradient(135deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.4))`,
                                    border: `1px solid ${isAttackBarbarian ? 'rgba(239, 68, 68, 0.3)' : 'rgba(139, 69, 19, 0.3)'}`,
                                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)',
                                    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.3)',
                                  }}
                                >
                                  {t('modals.globalAttack.target')}: {attackInfo.targetBuilding.toUpperCase()}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Defend Button for this attack */}
                          <button
                            onClick={() => handleDefend(attackKey, attackInfo)}
                            disabled={defending}
                            className="w-full px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-3.5 text-sm sm:text-base md:text-lg text-white rounded-lg sm:rounded-xl font-bold transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden group"
                            style={{
                              fontFamily: 'Tilt Warp',
                              background: defending
                                ? 'linear-gradient(135deg, rgba(60, 60, 60, 0.95), rgba(40, 40, 40, 0.95))'
                                : `linear-gradient(135deg, ${isAttackBarbarian ? 'rgba(200, 50, 50, 0.95)' : 'rgba(139, 69, 19, 0.95)'}, ${isAttackBarbarian ? 'rgba(150, 30, 30, 0.95)' : 'rgba(101, 67, 33, 0.95)'}, ${isAttackBarbarian ? 'rgba(200, 50, 50, 0.95)' : 'rgba(139, 69, 19, 0.95)'})`,
                              border: defending
                                ? '2px solid rgba(100, 100, 100, 0.4)'
                                : `2px solid ${isAttackBarbarian ? 'rgba(239, 68, 68, 0.8)' : 'rgba(139, 69, 19, 0.8)'}`,
                              boxShadow: defending
                                ? 'inset 0 2px 4px rgba(0, 0, 0, 0.5), 0 4px 8px rgba(0, 0, 0, 0.4)'
                                : `0 0 0 1px rgba(0, 0, 0, 0.8), 0 4px 12px ${isAttackBarbarian ? 'rgba(200, 50, 50, 0.5)' : 'rgba(139, 69, 19, 0.5)'}, 0 8px 24px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(0, 0, 0, 0.5)`,
                              textShadow: `0 2px 4px rgba(0, 0, 0, 1), 0 0 8px ${isAttackBarbarian ? 'rgba(200, 50, 50, 0.6)' : 'rgba(139, 69, 19, 0.6)'}`,
                              letterSpacing: '0.5px',
                            }}
                          >
                            {/* Animated combat effect overlay */}
                            {!defending && (
                              <div
                                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                style={{
                                  background: `linear-gradient(135deg, ${isAttackBarbarian ? 'rgba(255, 70, 70, 0.3)' : 'rgba(160, 90, 30, 0.3)'}, ${isAttackBarbarian ? 'rgba(200, 50, 50, 0.3)' : 'rgba(139, 69, 19, 0.3)'})`,
                                  boxShadow: `inset 0 0 30px ${isAttackBarbarian ? 'rgba(255, 70, 70, 0.4)' : 'rgba(160, 90, 30, 0.4)'}`,
                                }}
                              />
                            )}
                            <span className="relative z-10 uppercase tracking-wider">
                              {defending
                                ? t('modals.globalAttack.defending')
                                : attackInfo.type === 'barbarian'
                                  ? t('modals.globalAttack.defendMainLand')
                                  : t('modals.globalAttack.defendCastle', { castleId: attackInfo.castleId })}
                            </span>
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TailwindModal>
      )}
      {rewardData && (
        <Suspense fallback={null}>
          <LazyDefenseRewardAlert
            open={showRewardAlert}
            onClose={() => {
              setShowRewardAlert(false)
              setRewardData(null)
            }}
            reward={rewardData.reward}
            attackType={rewardData.attackType}
            castleId={rewardData.castleId}
          />
        </Suspense>
      )}

      {worldMapPveResultModal ? (
        <WorldMapAutoBattleResultModal
          open
          result={worldMapPveResultModal.result}
          type={worldMapPveResultModal.type}
          onClose={() => setWorldMapPveResultModal(null)}
        />
      ) : null}

      {mvmTowerAutoResolveModal?.won ? (
        <Suspense fallback={null}>
          <LazyMvMVictoryModal
            characterXp={mvmTowerAutoResolveModal.characterXp}
            userXp={mvmTowerAutoResolveModal.userXp}
            showResourceLoot={mvmTowerAutoResolveModal.showResourceLoot === true}
            seizedResources={mvmTowerAutoResolveModal.seizedResources ?? null}
            serverSummary={mvmTowerAutoResolveModal.message || null}
            overlayZIndex={MVM_AUTO_RESULT_OVERLAY_Z}
            conquerOffer={awaitCastleConquerOffer ? castleConquerOffer : null}
            awaitConquerOffer={awaitCastleConquerOffer}
            onConquer={handleGlobalCastleConquer}
            onRaid={handleGlobalCastleRaid}
            onClose={() => {
              setMvmTowerAutoResolveModal(null)
              setMvmAutoBattleProgressHud(null)
              castleConquerEligibleRef.current = false
              setCastleConquerOffer(null)
            }}
          />
        </Suspense>
      ) : null}
      {mvmTowerAutoResolveModal && !mvmTowerAutoResolveModal.won ? (
        <Suspense fallback={null}>
          <LazyMvMDefeatModal
            mode="standard"
            summaryOnly
            showBattleRewards
            seizedResources={null}
            serverSummary={mvmTowerAutoResolveModal.message || null}
            characterXp={mvmTowerAutoResolveModal.characterXp}
            userXp={mvmTowerAutoResolveModal.userXp}
            viewerBattleSide={mvmTowerAutoResolveModal.you}
            overlayZIndex={MVM_AUTO_RESULT_OVERLAY_Z}
            onClose={() => {
              setMvmTowerAutoResolveModal(null)
              setMvmAutoBattleProgressHud(null)
            }}
          />
        </Suspense>
      ) : null}

      {/* MvM incoming attack banner + persistent auto-battle progress (until battle-ended) */}
      {showMvmTopBannerStack && (
        <div
          /**
           * Marks the top strip this banner owns. It is fixed at z-index 9999, so anything else
           * anchored to the top centre — the notification ticker in particular — has to measure
           * this and sit below it, or an incoming-attack line renders underneath and is invisible
           * exactly when it matters most. See GlobalNotificationToastHost.
           */
          data-eclipsera-top-banner="1"
          style={
            towerHoldWaitingAttackerLayout
              ? {
                position: 'fixed',
                top: '50%',
                left: 0,
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                paddingLeft: 'max(12px, env(safe-area-inset-left, 0px))',
                transform: 'translateY(-50%)',
                maxWidth: '100vw',
                boxSizing: 'border-box',
                pointerEvents: 'none',
              }
              : {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 9999,
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: 'none',
              }
          }
        >
          <div
            style={
              towerHoldWaitingAttackerLayout
                ? {
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  padding: '8px 16px 8px 0',
                  maxWidth: 'min(560px, 92vw)',
                  width: '100%',
                }
                : {
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  padding: '10px 16px',
                  maxWidth: '600px',
                  width: '100%',
                }
            }
          >
            {shouldShowMvmBanner &&
              renderedMvmAttacks.map(([key, atk]) => {
                const nowBanner = Date.now()
                const isReturning = !!atk.attackerReturningBanner || !!atk.defenderReturningBanner
                const phaseLive =
                  atk.mvmPendingSnapshot != null
                    ? mvmPendingDisplayPhase(atk.mvmPendingSnapshot, nowBanner)
                    : atk.mvmCountdownPhase ?? (atk.rallyMode ? 'rally' : 'march')
                const remaining = getTimeRemaining(atk)
                const username = atk.attackerUsername || t('worldMap.unknown')
                const allianceName = atk.attackerAllianceName
                const allianceTag = allianceName ? t('worldMap.mvmBanner.allianceOf', { alliance: allianceName }) : ''
                const isCastleTarget =
                  !isReturning && !!address && !!atk.defenderWallet && normWallet(atk.defenderWallet) === normWallet(address)
                const isTowerBanner = !isReturning && atk.mvmAttackType === 'tower_pvp'
                const isVillageBanner = !isReturning && atk.mvmAttackType === 'barbarian_village'
                const countdownRally = !isReturning && atk.rallyMode && phaseLive === 'rally'
                const holdWaitingAttacker = towerDefenderHoldUntilAttackerResolves(atk, nowBanner)
                const defEndMs = atk.defenderArrivesAtIso ? new Date(atk.defenderArrivesAtIso).getTime() : NaN
                const defStillMarchingToTower = Number.isFinite(defEndMs) && nowBanner < defEndMs
                const defHoldsTower =
                  !!atk.towerMarchCommitted && Number.isFinite(defEndMs) && nowBanner >= defEndMs
                const canRecallTowerMarch =
                  defStillMarchingToTower &&
                  !['battle_started', 'battle_creating', 'returning', 'completed', 'cancelled'].includes(
                    String(atk.mvmStatusFromServer || ''),
                  )
                const attEndMs = atk.mvmPendingSnapshot?.arrivesAt
                  ? new Date(atk.mvmPendingSnapshot.arrivesAt).getTime()
                  : NaN
                const attackerStillApproaching =
                  Number.isFinite(attEndMs) && nowBanner < attEndMs && !holdWaitingAttacker
                const bannerTitle = isReturning
                  ? atk.defenderReturningBanner
                    ? t('worldMap.mvmBanner.defenderReturningTitle', {
                      defaultValue: 'Your champions are marching home from the tower.',
                    })
                    : t('worldMap.mvmBanner.returningTitle')
                  : isVillageBanner && isCastleTarget
                    ? t('worldMap.mvmBanner.villageMarchOnYours', {
                        defaultValue:
                          '{{leader}} is marching on your barbarian village — enter the battle when they arrive or it auto-resolves.',
                        leader: username,
                      })
                    : isTowerBanner && isCastleTarget
                    ? countdownRally
                      ? t('worldMap.mvmBanner.siegeYouRally', { leader: username, allianceTag })
                      : holdWaitingAttacker
                        ? t('worldMap.mvmBanner.towerHoldingWaitAttackerTitle', {
                          defaultValue: 'Your champions hold the tower — waiting for the attacker.',
                          leader: username,
                          allianceTag,
                        })
                        : defHoldsTower && attackerStillApproaching
                          ? t('worldMap.mvmBanner.towerHoldingAttackerEnRouteTitle', {
                            defaultValue: 'Your champions hold the tower — enemy column still approaching.',
                            leader: username,
                            allianceTag,
                          })
                          : t('worldMap.mvmBanner.towerMarchOnYours', {
                            defaultValue:
                              'Enemy march on your defense tower — you may deploy defenders (optional) or rely on auto-battle when they arrive.',
                            leader: username,
                            allianceTag,
                          })
                    : isTowerBanner && !isCastleTarget
                      ? t('worldMap.mvmBanner.towerMarchOnAlly', {
                        defaultValue: 'Alliance tower under march — ally defenders deploy from their castle.',
                        leader: username,
                      })
                      : isCastleTarget
                        ? countdownRally
                          ? t('worldMap.mvmBanner.siegeYouRally', { leader: username, allianceTag })
                          : t('worldMap.mvmBanner.siegeYouMarch', { leader: username, allianceTag })
                        : countdownRally
                          ? t('worldMap.mvmBanner.siegeAllyRally', { leader: username, allianceTag })
                          : t('worldMap.mvmBanner.siegeAllyMarch', { leader: username, allianceTag })
                const timerPrefix = holdWaitingAttacker
                  ? ''
                  : isReturning
                    ? t('worldMap.mvmBanner.timerHome')
                    : countdownRally
                      ? t('worldMap.mvmBanner.timerRally')
                      : t('worldMap.mvmBanner.timerArrive')
                return (
                  <div
                    key={key}
                    style={{
                      backgroundImage: `url(${MVM_ATTACK_NOTI_BG_URL})`,
                      backgroundSize: '100% 100%',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      borderRadius: '10px',
                      padding: '10px 18px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.45)',
                      pointerEvents: 'auto',
                      minHeight: 52,
                    }}
                  >
                    {/* Alarm / return icon */}
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                      {isReturning ? (
                        <>
                          <circle cx="12" cy="12" r="10" fill="rgba(80,180,120,0.85)" stroke="rgba(50,140,80,0.8)" strokeWidth="1" />
                          <path d="M15 8l-6 4 6 4V8z" fill="#fff" />
                        </>
                      ) : (
                        <>
                          <path d="M12 2L1 21h22L12 2z" fill="rgba(255,200,50,0.9)" stroke="rgba(200,150,0,0.8)" strokeWidth="1" />
                          <text x="12" y="17" textAnchor="middle" fill="#000" fontSize="12" fontWeight="bold">!</text>
                        </>
                      )}
                    </svg>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#fff', fontFamily: 'Tilt Warp, sans-serif', fontSize: '13px', fontWeight: 700, lineHeight: 1.3, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                        {bannerTitle}
                      </div>
                      {remaining && (
                        <div style={{ color: isReturning ? 'rgba(180,230,200,0.9)' : 'rgba(255,200,200,0.9)', fontFamily: 'Tilt Warp, sans-serif', fontSize: '12px', marginTop: '2px', textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
                          {timerPrefix}
                          {remaining}
                        </div>
                      )}
                      {isVillageBanner && isCastleTarget && ((atk.toQ != null && atk.toR != null) || hiddenVillageAttackCount > 0) && (
                        <div
                          style={{
                            color: 'rgba(255,228,200,0.9)',
                            fontFamily: 'Tilt Warp, sans-serif',
                            fontSize: '11px',
                            marginTop: '4px',
                            lineHeight: 1.35,
                            textShadow: '0 1px 2px rgba(0,0,0,0.55)',
                          }}
                        >
                          {atk.toQ != null && atk.toR != null
                            ? t('worldMap.mvmBanner.villageLocation', {
                              defaultValue: 'Village at ({{q}}, {{r}})',
                              q: atk.toQ,
                              r: atk.toR,
                            })
                            : ''}
                          {hiddenVillageAttackCount > 0
                            ? `${atk.toQ != null && atk.toR != null ? ' · ' : ''}${t('worldMap.mvmBanner.moreVillagesUnderAttack', {
                              defaultValue: '+{{count}} more under attack',
                              count: hiddenVillageAttackCount,
                            })}`
                            : ''}
                        </div>
                      )}
                      {isTowerBanner && isCastleTarget && !isReturning && !holdWaitingAttacker && !defHoldsTower && (
                        <div
                          style={{
                            color: 'rgba(255,235,200,0.88)',
                            fontFamily: 'Tilt Warp, sans-serif',
                            fontSize: '11px',
                            marginTop: '4px',
                            lineHeight: 1.35,
                            textShadow: '0 1px 2px rgba(0,0,0,0.55)',
                          }}
                        >
                          {atk.defenderBusyInOtherBattle
                            ? t('worldMap.mvmBanner.towerOptionalDefenseBusy', {
                              defaultValue:
                                'Defending is optional. You are in another battle — if you do nothing, your tower still auto-resolves when the enemy arrives.',
                            })
                            : t('worldMap.mvmBanner.towerOptionalDefense', {
                              defaultValue:
                                'Defending is optional. If you do not march, champions will still fight automatically when the attacker reaches the tower.',
                            })}
                        </div>
                      )}
                    </div>
                    {((isVillageBanner && isCastleTarget) || (!isVillageBanner && !isTowerBanner && !isReturning && isCastleTarget)) && (
                      <button
                        type="button"
                        aria-label={t('common.close', { defaultValue: 'Close' })}
                        title={t('common.close', { defaultValue: 'Close' })}
                        onClick={() => dismissBanner(atk)}
                        style={{
                          flexShrink: 0,
                          alignSelf: 'center',
                          width: 28,
                          height: 28,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '7px',
                          border: '1px solid rgba(200,160,70,0.25)',
                          background: 'rgba(8,5,2,0.6)',
                          backdropFilter: 'blur(6px)',
                          WebkitBackdropFilter: 'blur(6px)',
                          color: 'rgba(230,205,155,0.75)',
                          cursor: 'pointer',
                          padding: 0,
                          boxShadow: 'inset 0 1px 0 rgba(255,220,120,0.06)',
                        }}
                      >
                        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
                          <line x1="1.5" y1="1.5" x2="9.5" y2="9.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                          <line x1="9.5" y1="1.5" x2="1.5" y2="9.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                      </button>
                    )}
                    {!isReturning && atk.attackId && address && isCastleTarget && normWallet(atk.walletAddress) === normWallet(address) && (
                      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end', flexShrink: 0 }}>
                        {atk.mvmAttackType === 'tower_pvp' &&
                          !atk.towerMarchCommitted &&
                          !atk.defenderAttackerBusyAutoDefense &&
                          atk.mvmStatusFromServer === 'traveling' && (
                            <button
                              type="button"
                              onClick={() => {
                                setTowerMarchDeployAttackId(atk.attackId!)
                                setTowerMarchDeployOpen(true)
                              }}
                              disabled={towerMarchSubmitting}
                              style={{
                                flexShrink: 0,
                                padding: '10px 18px',
                                minHeight: 40,
                                borderRadius: '8px',
                                border: '1px solid rgba(212,175,86,0.55)',
                                background: 'rgba(40,32,18,0.92)',
                                color: '#f4e8b8',
                                fontFamily: 'Tilt Warp, sans-serif',
                                fontSize: '11px',
                                fontWeight: 700,
                                cursor: towerMarchSubmitting ? 'wait' : 'pointer',
                                opacity: towerMarchSubmitting ? 0.7 : 1,
                                textShadow: '0 1px 3px rgba(0,0,0,0.85)',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {t('worldMap.mvmTowerDeployButton', { defaultValue: 'March to defend (optional)' })}
                            </button>
                          )}
                        {atk.mvmAttackType === 'tower_pvp' && canRecallTowerMarch && atk.attackId && (
                          <button
                            type="button"
                            onClick={() => handleRecallDefenderTowerMarch(atk.attackId!)}
                            disabled={towerRecallBusyAttackId === atk.attackId}
                            style={{
                              flexShrink: 0,
                              padding: '10px 18px',
                              minHeight: 40,
                              borderRadius: '8px',
                              border: '1px solid rgba(120,160,220,0.55)',
                              background: 'rgba(22,28,44,0.92)',
                              color: '#d0e0ff',
                              fontFamily: 'Tilt Warp, sans-serif',
                              fontSize: '11px',
                              fontWeight: 700,
                              cursor: towerRecallBusyAttackId === atk.attackId ? 'wait' : 'pointer',
                              opacity: towerRecallBusyAttackId === atk.attackId ? 0.7 : 1,
                              textShadow: '0 1px 3px rgba(0,0,0,0.85)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {towerRecallBusyAttackId === atk.attackId
                              ? '…'
                              : t('worldMap.mvmTowerRecallButton', { defaultValue: 'Recall champions' })}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            {showAutoBattleProgressBanner && mvmAutoBattleProgressHud ? (
              <div
                key="mvm-auto-battle-progress"
                style={{
                  backgroundImage: `linear-gradient(135deg, rgba(45,35,75,0.97) 0%, rgba(25,20,50,0.98) 100%)`,
                  border: '1px solid rgba(180,140,255,0.45)',
                  borderRadius: '10px',
                  padding: '12px 18px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  boxShadow: '0 4px 24px rgba(80,60,160,0.35), 0 0 0 1px rgba(255,255,255,0.06) inset',
                  pointerEvents: 'auto',
                  minHeight: 52,
                  animation: 'mvmAutoBattleBannerPulse 2.2s ease-in-out infinite',
                }}
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                  <circle cx="12" cy="12" r="9" stroke="rgba(200,170,255,0.9)" strokeWidth="1.5" fill="rgba(100,70,200,0.35)" />
                  <path
                    d="M12 7v5l3 2"
                    stroke="rgba(230,210,255,0.95)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      color: 'rgba(230,215,255,0.98)',
                      fontFamily: 'Tilt Warp, sans-serif',
                      fontSize: '12px',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      textShadow: '0 1px 3px rgba(0,0,0,0.75)',
                    }}
                  >
                    {mvmAutoBattleProgressHud.attackType === 'tower_pvp'
                      ? t('mvm.autoBattleRemote.bannerTitleTower', {
                        defaultValue: 'Defense tower — auto battle',
                      })
                      : t('mvm.autoBattleRemote.bannerTitleCastle', {
                        defaultValue: 'Battle — auto resolution',
                      })}
                  </div>
                  <div
                    style={{
                      color: 'rgba(220,200,255,0.92)',
                      fontFamily: 'Tilt Warp, sans-serif',
                      fontSize: '13px',
                      marginTop: '4px',
                      lineHeight: 1.45,
                      textShadow: '0 1px 2px rgba(0,0,0,0.65)',
                    }}
                  >
                    {mvmAutoBattleProgressHud.line}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Defender choice modal: Auto or Enter Battle */}
      {villageDefense && !shouldHideAlert && location.pathname !== '/hex-battle' && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            background: 'rgba(0,0,0,0.72)',
            backdropFilter: 'blur(5px)',
            pointerEvents: 'auto',
          }}
        >
          <div
            className="relative w-full rounded-xl border-2 p-5"
            style={{
              maxWidth: 400,
              pointerEvents: 'auto',
              background: 'linear-gradient(135deg, rgba(40,28,16,0.98), rgba(26,18,11,0.98))',
              borderColor: 'rgba(234,179,8,0.6)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
              fontFamily: 'Tilt Warp, system-ui, sans-serif',
            }}
          >
            <h3 style={{ margin: '0 0 10px', textAlign: 'center', fontSize: 20, fontWeight: 700, color: '#f4e8b8' }}>
              {t('worldMap.villageUnderAttackTitle', { defaultValue: 'Your Barbarian Village is Under Attack!' })}
            </h3>
            <p style={{ margin: '0 0 16px', textAlign: 'center', fontSize: 13, lineHeight: 1.5, color: '#e6d49a' }}>
              {t('worldMap.villageUnderAttackBody', {
                attacker: villageDefense.attackerLabel || t('worldMap.unknown'),
                defaultValue: `${villageDefense.attackerLabel || 'An attacker'} has reached your village. Enter the battle to defend with your stationed champions, or let it auto-resolve.`,
              })}
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                disabled={villageDefenseSubmitting}
                onClick={async () => {
                  if (villageDefenseSubmitting) return
                  const vd = villageDefense
                  setVillageDefenseSubmitting(true)
                  try {
                    // Build the hex battle NOW (vs the stationed champions) and navigate in. The attacker is
                    // signalled to navigate via the `world:pve-march:hex-battle-started` socket.
                    const { battleId } = await enterBarbarianVillageDefense(vd.worldId, vd.villageId)
                    setVillageDefense(null)
                    navigate('/hex-battle', { replace: true, state: { battleId } })
                  } catch (e: any) {
                    const raw = String(e?.response?.data?.message || e?.message || '').trim()
                    const friendly =
                      raw === 'no_active_attack'
                        ? t('worldMap.villageNoActiveAttack', {
                            defaultValue:
                              'This attack is no longer active. Close and wait for the alert to refresh, or try Auto Battle.',
                          })
                        : raw === 'attack_in_progress'
                          ? t('worldMap.villageDefenseInProgress', {
                              defaultValue: 'Defense is already resolving — please wait a moment.',
                            })
                          : raw || t('worldMap.villageEnterBattleFail', { defaultValue: 'Could not enter the battle.' })
                    dispatch(onShowAlert(friendly, 'error'))
                  } finally {
                    setVillageDefenseSubmitting(false)
                  }
                }}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '2px solid rgba(34,197,94,0.8)',
                  background: 'rgba(34,197,94,0.25)',
                  color: '#eafff0',
                  fontWeight: 700,
                  cursor: villageDefenseSubmitting ? 'not-allowed' : 'pointer',
                  opacity: villageDefenseSubmitting ? 0.6 : 1,
                }}
              >
                {t('worldMap.villageEnterBattle', { defaultValue: 'Enter Battle' })}
              </button>
              <button
                type="button"
                disabled={villageDefenseSubmitting}
                onClick={async () => {
                  if (villageDefenseSubmitting) return
                  const vd = villageDefense
                  setVillageDefenseSubmitting(true)
                  try {
                    const result = await autoBarbarianVillageDefense(vd.worldId, vd.villageId)
                    if (result?.skipped) {
                      const reason = String(result.reason || '')
                      dispatch(
                        onShowAlert(
                          reason === 'attack_in_progress'
                            ? t('worldMap.villageDefenseInProgress', {
                                defaultValue: 'Defense is already resolving — please wait a moment.',
                              })
                            : t('worldMap.villageDefenseStale', {
                                defaultValue: 'This attack was already resolved or expired. The alert will refresh.',
                              }),
                          'warning',
                        ),
                      )
                      if (reason === 'no_active_march') {
                        setVillageDefense(null)
                      }
                      return
                    }
                    setVillageDefense(null)
                    if (result?.battleOutcome) {
                      const won =
                        result.battleOutcome === 'defender_holds' ||
                        String(result.battleOutcome).startsWith('defender')
                      dispatch(
                        onShowAlert(
                          won
                            ? t('worldMap.villageAutoBattleDefended', {
                                defaultValue: 'Your village repelled the attack.',
                              })
                            : t('worldMap.villageAutoBattleLost', {
                                defaultValue: 'Your village was conquered.',
                              }),
                          won ? 'success' : 'warning',
                        ),
                      )
                    }
                  } catch (e: any) {
                    const raw = String(e?.response?.data?.message || e?.message || '').trim()
                    const friendly =
                      raw === 'no_active_attack'
                        ? t('worldMap.villageNoActiveAttack', {
                            defaultValue:
                              'This attack is no longer active. Close and wait for the alert to refresh.',
                          })
                        : raw === 'attack_in_progress'
                          ? t('worldMap.villageDefenseInProgress', {
                              defaultValue: 'Defense is already resolving — please wait a moment.',
                            })
                          : raw || t('worldMap.villageAutoBattleFail', { defaultValue: 'Could not start auto battle.' })
                    dispatch(onShowAlert(friendly, 'error'))
                  } finally {
                    setVillageDefenseSubmitting(false)
                  }
                }}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '2px solid rgba(150,150,150,0.7)',
                  background: 'rgba(100,100,100,0.25)',
                  color: '#eee',
                  fontWeight: 700,
                  cursor: villageDefenseSubmitting ? 'not-allowed' : 'pointer',
                  opacity: villageDefenseSubmitting ? 0.6 : 1,
                }}
              >
                {t('worldMap.villageAutoBattle', { defaultValue: 'Auto Battle' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {defenderChoiceOpen && !shouldHideAlert && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            background: 'rgba(0,0,0,0.72)',
            backdropFilter: 'blur(5px)',
          }}
        >
          {(() => {
            const decorH = MVM_ALLIANCE_BATTLE_PANEL_DECOR_H_PX
            const decorOut = MVM_ALLIANCE_BATTLE_PANEL_DECOR_OUTSET_PX
            const topBarUp = decorOut + MVM_ALLIANCE_BATTLE_PANEL_TOP_EXTRA_UP_PX
            const contentPadTop = Math.max(8, decorH - decorOut + 4 - Math.floor(MVM_ALLIANCE_BATTLE_PANEL_TOP_EXTRA_UP_PX / 2))
            const contentPadBottom = Math.max(10, decorH - decorOut + 4)
            const goldTitle: React.CSSProperties = {
              fontFamily: 'Tilt Warp, system-ui, sans-serif',
              fontSize: 22,
              fontWeight: 700,
              margin: '0 0 14px',
              lineHeight: 1.25,
              textAlign: 'center',
              color: '#f4e8b8',
              textShadow:
                '0 1px 0 rgba(100,65,18,0.95), 0 0 28px rgba(212,175,86,0.45), 0 2px 6px rgba(0,0,0,0.9)',
            }
            const goldBody: React.CSSProperties = {
              fontFamily: 'Tilt Warp, system-ui, sans-serif',
              fontSize: 14,
              lineHeight: 1.55,
              margin: 0,
              textAlign: 'center',
              color: '#e6d49a',
              textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 0 12px rgba(180,140,50,0.25)',
            }
            const goldMuted: React.CSSProperties = {
              ...goldBody,
              fontSize: 12,
              opacity: 0.92,
              color: '#dcc88a',
            }
            return (
              <div
                className="relative w-full max-w-md shadow-2xl"
                style={{ maxWidth: 440, filter: 'drop-shadow(0 12px 40px rgba(0,0,0,0.65))' }}
              >
                <div
                  className="absolute inset-0 z-0 overflow-hidden rounded-sm"
                  style={{
                    backgroundImage: `url(${MVM_INCOMING_ATTACK_PANEL_BG_URL})`,
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                  }}
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute left-0 right-0 z-[1]"
                  style={{
                    top: -topBarUp,
                    height: decorH,
                    backgroundImage: `url(${MVM_INCOMING_ATTACK_PANEL_TOP_URL})`,
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center top',
                  }}
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute left-0 right-0 z-[1]"
                  style={{
                    bottom: -decorOut,
                    height: decorH,
                    backgroundImage: `url(${MVM_INCOMING_ATTACK_PANEL_BOTTOM_URL})`,
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center bottom',
                  }}
                />
                <div
                  className="relative z-[2] flex flex-col items-stretch"
                  style={{ padding: `${contentPadTop}px 28px ${contentPadBottom}px` }}
                >
                  <h3 style={goldTitle}>
                    {t('modals.globalAttack.mvmDefenderIncomingTitle')}
                  </h3>
                  <p style={{ ...goldBody, marginBottom: 16 }}>
                    {t('modals.globalAttack.mvmDefenderIncomingLead')}
                  </p>
                  <div
                    aria-hidden
                    style={{
                      height: 1,
                      margin: '0 0 16px',
                      background:
                        'linear-gradient(90deg, transparent 0%, rgba(212,175,86,0.55) 20%, rgba(245,220,150,0.75) 50%, rgba(212,175,86,0.55) 80%, transparent 100%)',
                      boxShadow: '0 1px 0 rgba(0,0,0,0.35)',
                    }}
                  />
                  <p style={{ ...goldMuted, marginBottom: 22 }}>
                    {t('modals.globalAttack.mvmDefenderIncomingOffline')}
                  </p>
                  <div className="flex w-full flex-col gap-3">
                    <button
                      type="button"
                      onClick={handleDefenderEnterBattle}
                      disabled={defenderSubmitting}
                      style={{
                        ...mvmDefeatBackButtonStyle,
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '14px 18px',
                        fontSize: 15,
                        letterSpacing: '0.04em',
                        cursor: defenderSubmitting ? 'not-allowed' : 'pointer',
                        opacity: defenderSubmitting ? 0.6 : 1,
                      }}
                    >
                      {t('modals.globalAttack.mvmDefenderEnterBattle')}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDefenderAutoDefend()}
                      disabled={defenderSubmitting}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '14px 18px',
                        fontSize: 15,
                        letterSpacing: '0.04em',
                        cursor: defenderSubmitting ? 'not-allowed' : 'pointer',
                        opacity: defenderSubmitting ? 0.6 : 1,
                        fontFamily: 'Tilt Warp, system-ui, sans-serif',
                        fontWeight: 700,
                        borderRadius: 10,
                        border: '2px solid rgba(212,175,86,0.65)',
                        background: 'linear-gradient(180deg, rgba(40,32,22,0.95) 0%, rgba(22,18,12,0.98) 100%)',
                        color: '#f0e6c8',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,220,160,0.12)',
                      }}
                    >
                      {t('modals.globalAttack.mvmDefenderAutoDefend')}
                    </button>
                  </div>
                </div>
              </div>
            )
          })()}
        </div>
      )}

      {/* Defender manual character selection (after choosing "Enter Battle") */}
      {defenderManualModalOpen && !shouldHideAlert && (
        <Suspense fallback={null}>
          <LazyAttackCharacterSelectionModal
            open={defenderManualModalOpen}
            setOpen={(open) => {
              setDefenderManualModalOpen(open)
              if (!open) {
                defenderPickingCharactersRef.current = null
                setDefenderAttackId(null)
                setDefenderMvmAttackType('castle')
              }
            }}
            onSelectCharacters={handleDefenderCharacterSelect}
            maxCharacters={MAIN_PARTICIPANT_MAX_CHARACTERS}
            isSubmittingAttack={defenderSubmitting}
            breadSupplyContext={{ kind: 'defender_choice' }}
            disableDismissal
          />
        </Suspense>
      )}

      {towerMarchDeployOpen && (
        <Suspense fallback={null}>
          <LazyAttackCharacterSelectionModal
            open={towerMarchDeployOpen}
            setOpen={(open) => {
              setTowerMarchDeployOpen(open)
              if (!open) setTowerMarchDeployAttackId(null)
            }}
            onSelectCharacters={handleTowerMarchCharacterSelect}
            maxCharacters={MAIN_PARTICIPANT_MAX_CHARACTERS}
            isSubmittingAttack={towerMarchSubmitting}
          />
        </Suspense>
      )}

      <TailwindModal
        open={!!mvmRallyInvite}
        onClose={() => setMvmRallyInvite(null)}
        zIndex={10020}
        backdropStyle={{ backgroundColor: 'rgba(0,0,0,.65)' }}
      >
        <div
          className="relative mx-auto max-w-md rounded-xl border-2 border-amber-700/60 bg-gradient-to-b from-amber-950/95 to-stone-950/95 p-6 text-center shadow-2xl"
          style={{ fontFamily: 'Tilt Warp, sans-serif' }}
        >
          <h3 className="text-lg font-bold text-amber-100 mb-2">Alliance rally</h3>
          <p className="text-sm text-amber-200/90 mb-1">
            {mvmRallyInvite?.leaderName || 'A teammate'} is rallying an attack
            {mvmRallyInvite?.targetName ? ` on ${mvmRallyInvite.targetName}` : ''}.
          </p>
          {(mvmRallyInvite?.arrivesAt || mvmRallyInvite?.rallyPhaseEndAt) && (
            <p className="text-xs text-amber-300/90 mb-2">
              {t('worldMap.mvmRallyJoinUntil', {
                defaultValue: 'You can join until the host reaches the target: {{when}}',
                when: new Date(mvmRallyInvite.arrivesAt || mvmRallyInvite.rallyPhaseEndAt || '').toLocaleString(),
              })}
            </p>
          )}
          <p className="text-xs text-stone-400 mb-5">
            {t('worldMap.mvmRallyJoinHint', {
              defaultValue:
                'Up to {{max}} characters. Your force marches from your own castle and fights beside the host. Dismissed this? The rally mail keeps a Join attack button for as long as the host is on the road.',
              max: ALLIED_PARTICIPANT_MAX_CHARACTERS,
            })}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              type="button"
              className="px-4 py-2 rounded-lg border border-stone-600 text-stone-300 text-sm"
              onClick={() => setMvmRallyInvite(null)}
            >
              Dismiss
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-lg bg-amber-700 hover:bg-amber-600 text-white text-sm font-bold"
              onClick={() => {
                if (!mvmRallyInvite) return
                rallyJoinAttackIdRef.current = mvmRallyInvite.attackId
                const ms =
                  typeof mvmRallyInvite.marchTravelTimeMs === 'number' &&
                  Number.isFinite(mvmRallyInvite.marchTravelTimeMs)
                    ? mvmRallyInvite.marchTravelTimeMs
                    : 15_000
                setRallyJoinMarchMs(ms)
                const lc =
                  typeof mvmRallyInvite.leaderCharacterCount === 'number' && mvmRallyInvite.leaderCharacterCount > 0
                    ? mvmRallyInvite.leaderCharacterCount
                    : 1
                setRallyJoinLeaderMinBread(
                  typeof mvmRallyInvite.rallyJoinMinBread === 'number' &&
                    Number.isFinite(mvmRallyInvite.rallyJoinMinBread)
                    ? mvmRallyInvite.rallyJoinMinBread
                    : mvmMinCastleAttackerBreadLoaves(lc, ms),
                )
                setMvmRallyInvite(null)
                setMvmRallyJoinModalOpen(true)
              }}
            >
              Join rally
            </button>
          </div>
        </div>
      </TailwindModal>

      {mvmRallyJoinModalOpen && (
        <Suspense fallback={null}>
          <LazyAttackCharacterSelectionModal
            open={mvmRallyJoinModalOpen}
            setOpen={(open) => {
              setMvmRallyJoinModalOpen(open)
              if (!open) {
                rallyJoinAttackIdRef.current = null
                setRallyJoinLeaderMinBread(0)
              }
            }}
            onSelectCharacters={handleRallyJoinCharacters}
            maxCharacters={ALLIED_PARTICIPANT_MAX_CHARACTERS}
            confirmButtonLabel="Join rally"
            isSubmittingAttack={rallyJoinSubmitting}
            breadSupplyContext={{ kind: 'rally_join', marchTimeMs: rallyJoinMarchMs, leaderMinBreadLoaves: rallyJoinLeaderMinBread }}
          />
        </Suspense>
      )}

      <TailwindModal
        open={!!mvmReinforceInvite}
        onClose={() => setMvmReinforceInvite(null)}
        zIndex={10020}
        backdropStyle={{ backgroundColor: 'rgba(0,0,0,.65)' }}
      >
        <div
          className="relative mx-auto max-w-md rounded-xl border-2 border-sky-700/50 bg-gradient-to-b from-slate-900/95 to-slate-950/95 p-6 text-center shadow-2xl"
          style={{ fontFamily: 'Tilt Warp, sans-serif' }}
        >
          <h3 className="text-lg font-bold text-sky-100 mb-2">
            {mvmReinforceInvite?.helpRequestedByDefender
              ? 'Ally asked for your help'
              : 'Reinforce alliance castle'}
          </h3>
          <p className="text-sm text-sky-200/85 mb-2">
            {mvmReinforceInvite?.message
              || 'An ally castle is under threat. Tap OK to choose defenders and join the same battle.'}
          </p>
          {mvmReinforceInvite?.arrivesAt && (
            <p className="text-xs text-slate-400 mb-4">
              March / rally ends around: {new Date(mvmReinforceInvite.arrivesAt).toLocaleString()}
            </p>
          )}
          <p className="text-xs text-slate-500 mb-5">
            Up to {ALLIED_PARTICIPANT_MAX_CHARACTERS} characters — same alliance as the castle owner. After you confirm, you are registered for this defense; when the battle starts you will get a prompt to enter the hex field.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              type="button"
              className="px-4 py-2 rounded-lg border border-slate-600 text-slate-300 text-sm"
              onClick={() => setMvmReinforceInvite(null)}
            >
              Not now
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-lg bg-sky-700 hover:bg-sky-600 text-white text-sm font-bold"
              onClick={() => {
                if (!mvmReinforceInvite) return
                const inv = mvmReinforceInvite
                reinforceAttackIdRef.current = inv.attackId
                setReinforceMarchMs(15_000)
                setMvmReinforceInvite(null)
                setMvmReinforceModalOpen(true)
                /** Size the bread input from this ally's own march time to the besieged castle. */
                if (address && inv.defenderWallet) {
                  mvmWorldAttackPrecheck(address, inv.defenderWallet, {
                    castleSiegePrecheck: true,
                    ...(inv.worldId ? { worldId: inv.worldId } : {}),
                  })
                    .then((data: { marchTimeMs?: number }) => {
                      const ms = Number(data?.marchTimeMs)
                      if (Number.isFinite(ms) && ms > 0) setReinforceMarchMs(ms)
                    })
                    .catch(() => {})
                }
              }}
            >
              OK — help defend
            </button>
          </div>
        </div>
      </TailwindModal>

      {mvmReinforceModalOpen && (
        <Suspense fallback={null}>
          <LazyAttackCharacterSelectionModal
            open={mvmReinforceModalOpen}
            setOpen={(open) => {
              setMvmReinforceModalOpen(open)
              if (!open) reinforceAttackIdRef.current = null
            }}
            onSelectCharacters={handleReinforceCharacters}
            maxCharacters={ALLIED_PARTICIPANT_MAX_CHARACTERS}
            confirmButtonLabel="Join defense roster"
            isSubmittingAttack={reinforceSubmitting}
            breadSupplyContext={{ kind: 'reinforce', marchTimeMs: reinforceMarchMs }}
          />
        </Suspense>
      )}

      {/* Ransom request modal — shown to the owner of the captured character */}
      {ransomRequests.length > 0 && (() => {
        const req = ransomRequests[0]
        const handleRespond = async (accept: boolean) => {
          if (!address || ransomRespondingId) return
          setRansomRespondingId(req.ransomId)
          try {
            const res = (await respondToRansomRequestApi(address, req.ransomId, accept)) as any

            // Server returned a soft failure (e.g. insufficient resources)
            if (res?.success === false) {
              const errMsg = res?.message || 'Action failed.'
              if (accept) {
                // Keep modal open so owner can decline instead; show blocking alert
                alert.open(errMsg)
                dispatch(addNotification({
                  id: `ransom-fail-${req.ransomId}-${Date.now()}`,
                  title: 'Ransom Failed',
                  walletAddress: address,
                  message: errMsg,
                  type: 'war',
                  timestamp: Date.now(),
                  read: false,
                }))
              } else {
                setRansomRequests(prev => prev.filter(r => r.ransomId !== req.ransomId))
              }
              return
            }

            // Success
            setRansomRequests(prev => prev.filter(r => r.ransomId !== req.ransomId))
            if (accept) {
              const msg = `You paid ransom — ${req.characterName || 'your character'} has been returned.`
              dispatch(onShowAlert(msg, 'success'))
              dispatch(addNotification({
                id: `ransom-paid-${req.ransomId}`,
                title: 'Ransom Paid',
                walletAddress: address,
                message: msg,
                type: 'war',
                timestamp: Date.now(),
                read: false,
              }))
              // Update owner's resources (deducted) and unlock the returned character
              if (!syncResourcesFromResponse(dispatch as any, res)) {
                dispatch(getResources(address, null, () => {}) as any)
              }
              dispatch(fetchCharacters({ walletAddress: address, nativeCastle: 0 }) as any)
            }
          } catch (err: any) {
            const errMsg = err?.response?.data?.message || err?.message || 'Action failed.'
            if (accept) {
              // Keep modal open — owner needs to see why accept failed (e.g. not enough resources)
              alert.open(errMsg)
              dispatch(addNotification({
                id: `ransom-fail-${req.ransomId}-${Date.now()}`,
                title: 'Ransom Failed',
                walletAddress: address,
                message: errMsg,
                type: 'war',
                timestamp: Date.now(),
                read: false,
              }))
            } else {
              // Decline failed — just dismiss silently
              setRansomRequests(prev => prev.filter(r => r.ransomId !== req.ransomId))
            }
          } finally {
            setRansomRespondingId(null)
          }
        }
        const LAND_BLOB = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land'
        const RANSOM_RESOURCE_ROWS = [
          { key: 'wheat', label: 'Wheat', icon: `${LAND_BLOB}/wheat-icon.webp` },
          { key: 'iron',  label: 'Iron',  icon: `${LAND_BLOB}/iron-icon.webp`  },
          { key: 'wood',  label: 'Wood',  icon: `${LAND_BLOB}/wood-icon.webp`  },
          { key: 'water', label: 'Water', icon: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/_merged_/Eclipsera/water_reward_icon.webp' },
          { key: 'stone', label: 'Stone', icon: `${LAND_BLOB}/stone-icon.webp` },
          { key: 'bread', label: 'Bread', icon: `${LAND_BLOB}/bread-icon.webp` },
        ] as const
        const requestedResources = RANSOM_RESOURCE_ROWS.filter(
          r => ((req.resources as Record<string, number>)[r.key] || 0) > 0,
        )
        const bgFill = { backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat' as const, backgroundPosition: 'center' as const }
        return (
          <div
            className="fixed inset-0 z-[19000] flex items-center justify-center p-3"
            style={{ background: 'rgba(0,0,0,0.78)' }}
          >
            <div
              className="relative flex w-full max-w-[820px] flex-col overflow-hidden rounded-2xl"
              style={{
                backgroundImage: 'url(https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/prisoners/release-modal/modal-bg.webp)',
                ...bgFill,
                boxShadow: '0 24px 80px rgba(0,0,0,0.9)',
                minHeight: 460,
              }}
            >
              {/* Close / Decline button */}
              <button
                type="button"
                onClick={() => handleRespond(false)}
                aria-label="Decline"
                className="absolute right-3 top-3 z-30 flex h-8 w-8 items-center justify-center rounded border border-[#a07830]/60 bg-[#0e0c08]/80 text-[#b8a882] hover:text-white"
              >
                <svg viewBox="0 0 14 14" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M2 2l10 10M12 2L2 12" />
                </svg>
              </button>

              {/* Title */}
              <div className="px-6 pb-1 pt-6 text-center">
                <div className="font-serif text-2xl font-bold tracking-[0.25em] text-[#e8c97c] drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">
                  RANSOM REQUEST
                </div>
                <div className="mt-1 font-['Tilt_Warp'] text-[12px] text-[#b8a882]">
                  {req.holderNick} requests to release {req.characterName || 'your character'} for the following cost.
                </div>
              </div>

              {/* Body: character portrait + resource display */}
              <div className="flex min-h-0 gap-5 px-5 pb-5 pt-4">

                {/* Left: character portrait card */}
                <div
                  className="relative hidden shrink-0 flex-col overflow-hidden rounded-xl sm:flex"
                  style={{ width: 220, backgroundImage: 'url(https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/prisoners/card-bg.webp)', ...bgFill }}
                >
                  <div className="pointer-events-none absolute inset-0 z-20" style={{ backgroundImage: 'url(https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/prisoners/card-border.webp)', ...bgFill }} aria-hidden />
                  <div className="relative overflow-hidden" style={{ height: 240, backgroundImage: 'url(https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/prisoners/prisoner-bg.webp)', ...bgFill }}>
                    {req.characterImg
                      ? <img src={req.characterImg} alt="" className="absolute inset-0 h-full w-full object-cover object-top" />
                      : <div className="absolute inset-0 bg-black/40" />}
                    <div className="pointer-events-none absolute inset-0 z-10" style={{ backgroundImage: 'url(https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/prisoners/prisoner-border.webp)', ...bgFill }} aria-hidden />
                    <div className="pointer-events-none absolute inset-0 z-[11]" style={{ backgroundImage: 'url(https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/prisoners/iron-barred.webp)', ...bgFill }} aria-hidden />
                  </div>
                  <div className="relative z-10 flex flex-col gap-2 px-3 pb-3 pt-2.5">
                    <div className="truncate text-center font-['Tilt_Warp'] text-[13px] font-bold text-[#f0e6d2]">
                      {req.characterName || '—'}
                    </div>
                    <div className="truncate text-center font-['Tilt_Warp'] text-[10px] text-[#b8a882]">
                      Held by: {req.holderNick}
                    </div>
                  </div>
                </div>

                {/* Right: resource display + buttons */}
                <div className="flex min-w-0 flex-1 flex-col gap-3">

                  {/* "RELEASE COST" divider */}
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1" style={{ background: 'linear-gradient(to left, rgba(212,168,75,0.5), transparent)' }} />
                    <div className="font-['Tilt_Warp'] text-[10px] font-bold tracking-[0.22em] text-[#e8c97c]">
                      RELEASE COST
                    </div>
                    <div className="h-px flex-1" style={{ background: 'linear-gradient(to right, rgba(212,168,75,0.5), transparent)' }} />
                  </div>

                  {/* Resource rows — display only, no input */}
                  {requestedResources.length > 0 ? (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                      {requestedResources.map(({ key, label, icon }) => {
                        const amount = (req.resources as Record<string, number>)[key] || 0
                        return (
                          <div key={key} className="flex items-center gap-2">
                            <img src={icon} alt={label} className="h-5 w-5 shrink-0 object-contain drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]" />
                            <div className="w-10 shrink-0 font-['Tilt_Warp'] text-[11px] font-bold text-[#c8b890]">{label}</div>
                            <div
                              className="flex flex-1 items-center justify-center overflow-hidden rounded border py-1.5"
                              style={{ borderColor: 'rgba(160,120,48,0.4)', background: 'rgba(0,0,0,0.45)' }}
                            >
                              <span className="font-['Tilt_Warp'] text-[12px] text-[#f0e6d2]">{amount.toLocaleString()}</span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-1 items-center justify-center">
                      <span className="font-['Tilt_Warp'] text-[12px] text-[#7a6a4a]">No cost specified</span>
                    </div>
                  )}

                  {/* Note */}
                  <div className="flex items-start gap-2 rounded-lg border border-[#a07830]/25 bg-black/30 px-3 py-3">
                    <svg viewBox="0 0 16 16" className="mt-px h-3.5 w-3.5 shrink-0 text-[#e8c97c]" fill="currentColor">
                      <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 3.5a.75.75 0 110 1.5.75.75 0 010-1.5zM7.25 7h1.5v4.5h-1.5V7z" />
                    </svg>
                    <p className="font-['Tilt_Warp'] text-[12px] leading-relaxed text-[#7a6a4a]">
                      Accepting will deduct these resources from your account and release the character.
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-center gap-12 pb-1 pt-2">
                    <button
                      type="button"
                      disabled={ransomRespondingId !== null}
                      onClick={() => handleRespond(false)}
                      className="relative flex w-36 items-center justify-center overflow-hidden rounded-xl py-3 font-['Tilt_Warp'] text-[17px] font-bold text-[#f0e6d2] transition-[filter,opacity] disabled:opacity-50 hover:brightness-110"
                      style={{ backgroundImage: 'url(https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/prisoners/kill-bg.webp)', ...bgFill }}
                    >
                      Decline
                    </button>
                    <button
                      type="button"
                      disabled={ransomRespondingId !== null}
                      onClick={() => handleRespond(true)}
                      className="relative flex w-36 items-center justify-center overflow-hidden rounded-xl py-3 font-['Tilt_Warp'] text-[14px] font-bold text-[#f0e6d2] transition-[filter,opacity] disabled:opacity-50 hover:brightness-110"
                      style={{ backgroundImage: 'url(https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/prisoners/release-bg.webp)', ...bgFill }}
                    >
                      {ransomRespondingId === req.ransomId ? '…' : 'Accept'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      })()}
    </>
  )
}

export default GlobalAttackAlert


